import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  LinearProgress,
  CircularProgress,
  Checkbox,
  Switch,
  Toolbar,
  Tooltip,
  Collapse,
  Tabs,
  Tab,
  Autocomplete,
  ListSubheader,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  CloudUpload,
  Edit,
  Delete,
  Search,
  MusicNote,
  ArrowUpward,
  ArrowDownward,
  PlayArrow,
  Pause,
  CloudSync,
  Link as LinkIcon,
  LinkOff,
  Download,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  Close,
  ExpandMore,
  ExpandLess,
  LibraryAdd,
  DoneAll,
  Assessment as ReportIcon,
  PictureAsPdf,
  ChevronLeft,
  ChevronRight,
  Public,
  Delete as DeleteIcon,
  Schedule,
  Dns,
  PlayCircleOutline,
  CheckCircleOutline,
  ErrorOutline as SyncErrorIcon,
  Storage,
  QueueMusic,
  UploadFile,
  WarningAmber,
  Album,
  DragIndicator,
} from '@mui/icons-material';
import { Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import PageHeader from '../components/PageHeader';
import AudioEditor from '../components/AudioEditor';
import BeatmixManager from '../components/BeatmixManager';
import RekordboxInfoDialog from '../components/RekordboxInfoDialog';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  buildCatalogueDocument,
  buildPlaybackReportModel,
  catalogueFilename,
  catalogueStationName,
  pdfSafe,
  pdfSafeRows,
  playbackWorkbook,
  saveCatalogueExcel,
} from '../utils/libraryCatalogueExport';
import { saveExcelWorkbook } from '../utils/excelWorkbook';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const REPLACE_AUDIO_POLL_MS = 2000;
const REPLACE_AUDIO_POLL_MAX_ERRORS = 5;
const LIBRARY_COLUMN_ORDER_KEY = 'library_name_column_order';

function replaceAudioJobActive(job) {
  return job && (job.status === 'queued' || job.status === 'processing');
}

function replaceJobFor(jobs, assetId) {
  if (assetId == null || !jobs) return null;
  return jobs[assetId] || jobs[String(assetId)] || null;
}

function mixCueProblemTooltip(problem) {
  const marks = problem.markers
    .map((marker) => `mark ${marker.mark} at ${Number(marker.sec).toFixed(1)}s`)
    .join(', ');
  const pastEnd = `Rekordbox ${marks} — past the end of this ${Number(problem.fileDurationSec).toFixed(1)}s file.`;
  if (problem.ignored) {
    return `${pastEnd} The cue points and beatgrid were made on another version of the track, so playout ignores them and this track does not beatmix. Set the cues on this file in Rekordbox and re-import them.`;
  }
  return `${pastEnd} Playout ends the mix-out at the end of the file; check this cue in Rekordbox.`;
}

function replaceAudioStatusTitle(job, uploadingPct) {
  if (!job) return 'Replace audio';
  if (job.status === 'uploading') return `Uploading ${uploadingPct}%`;
  if (job.status === 'queued') return 'Queued for conversion';
  if (job.status === 'processing') return 'Converting in the background';
  return 'Replace audio';
}

function activeReplacePollKey(jobs) {
  return Object.values(jobs || {})
    .filter((job) => job?.jobId && replaceAudioJobActive(job))
    .map((job) => `${job.assetId}:${job.jobId}`)
    .sort()
    .join(',');
}
const LIBRARY_COLUMN_ORDERS = {
  TITLE_ARTIST: 'title-artist',
  ARTIST_TITLE: 'artist-title',
  CUSTOM: 'custom',
};
// Only these columns can be dragged. Duration, Dropbox and Actions stay pinned
// on the right so the table never loses its fixed columns.
const LIBRARY_COLUMN_KEYS = ['title', 'artist', 'categories'];
const DEFAULT_LIBRARY_COLUMN_ORDER = ['title', 'artist', 'categories'];
const LIBRARY_COLUMN_PRESETS = {
  [LIBRARY_COLUMN_ORDERS.TITLE_ARTIST]: ['title', 'artist', 'categories'],
  [LIBRARY_COLUMN_ORDERS.ARTIST_TITLE]: ['artist', 'title', 'categories'],
};

const normalizeLibraryColumnOrder = (value) => {
  let keys = [];
  if (Array.isArray(value)) {
    keys = value;
  } else if (typeof value === 'string' && value) {
    if (LIBRARY_COLUMN_PRESETS[value]) {
      keys = LIBRARY_COLUMN_PRESETS[value];
    } else {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) keys = parsed;
      } catch (_) {
        // Legacy/unknown value: fall back to the default order below.
        keys = [];
      }
    }
  }
  const order = [];
  keys.forEach((key) => {
    if (LIBRARY_COLUMN_KEYS.includes(key) && !order.includes(key)) order.push(key);
  });
  // Any column missing from the saved value is appended, so new columns appear.
  LIBRARY_COLUMN_KEYS.forEach((key) => {
    if (!order.includes(key)) order.push(key);
  });
  return order;
};

const readLibraryColumnOrder = () => {
  try {
    const saved = localStorage.getItem(LIBRARY_COLUMN_ORDER_KEY);
    if (saved) return normalizeLibraryColumnOrder(saved);
  } catch (_) {
    // Ignore storage access issues and fall back to default.
  }
  return [...DEFAULT_LIBRARY_COLUMN_ORDER];
};
const formatLocalDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatReportAssetLabel = (asset) => {
  if (!asset) return '';
  const title = asset.title || 'Untitled';
  return asset.artist ? `${title} — ${asset.artist}` : title;
};
const formatReportPlays = (asset) => {
  const plays = Number(asset?.play_count);
  if (!Number.isFinite(plays)) return '';
  return `${plays} play${plays === 1 ? '' : 's'}`;
};
// Same fields the server searches (title, artist, asset number), so typing in
// the picker narrows the list the same way the server already did.
const reportAssetFilterOptions = createFilterOptions({
  stringify: (asset) => `#${asset.id} ${asset.title || ''} ${asset.artist || ''}`,
  limit: 50,
});
const REPORT_SEARCH_DEBOUNCE_MS = 300;
const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];
const CATEGORY_TYPE_ORDER = ['music', 'station_id', 'music_bed', 'other'];
const CATEGORY_TYPE_LABELS = {
  music: 'Music',
  station_id: 'Station / ID',
  music_bed: 'Music Bed',
  other: 'Other',
};
const CATEGORY_TYPE_FILTER_THRESHOLD = 8;
const getCategoryType = (category) => {
  if (!category?.category_type || category.category_type === 'other') return 'other';
  return category.category_type;
};
const filterCategoriesByType = (categoryList, categoryType) => {
  if (!categoryType) return categoryList || [];
  return (categoryList || []).filter((category) => getCategoryType(category) === categoryType);
};
const groupCategoriesByType = (categoryList) => {
  const groups = {};
  (categoryList || []).forEach((category) => {
    const type = getCategoryType(category);
    if (!groups[type]) groups[type] = [];
    groups[type].push(category);
  });
  return [
    ...CATEGORY_TYPE_ORDER.filter((type) => groups[type]?.length),
    ...Object.keys(groups).filter((type) => !CATEGORY_TYPE_ORDER.includes(type)),
  ].map((type) => ({
    type,
    label: CATEGORY_TYPE_LABELS[type] || type,
    categories: groups[type],
  }));
};
function CategoryTypeSelect({ value, onChange, sx, size, fullWidth = false }) {
  return (
    <FormControl size={size} fullWidth={fullWidth} sx={sx}>
      <InputLabel>Category Type</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label="Category Type"
      >
        <MenuItem value="">All Types</MenuItem>
        {CATEGORY_TYPE_ORDER.map((type) => (
          <MenuItem key={type} value={type}>
            {CATEGORY_TYPE_LABELS[type]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function renderCataloguePdf(catalogue, labels, filename) {
  const model = buildCatalogueDocument(catalogue, labels);
  const replacedCharacters = pdfSafe(JSON.stringify(model)) !== JSON.stringify(model);
  const doc = new jsPDF({ orientation: 'landscape' });
  const tableStyles = {
    theme: 'grid',
    headStyles: { fillColor: [0, 180, 230], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  };

  doc.setFontSize(16);
  doc.setTextColor(0, 180, 230);
  doc.text(pdfSafe(model.title), 14, 16);
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const subtitle = doc.splitTextToSize(pdfSafe(model.subtitle), 268);
  doc.text(subtitle, 14, 23);
  let cursor = 23 + (subtitle.length * 4);
  const noteText = pdfSafe(model.note) + (replacedCharacters
    ? ' Characters this PDF font cannot draw are shown as ?.'
    : '');
  const note = doc.splitTextToSize(noteText, 268);
  doc.text(note, 14, cursor);
  cursor += (note.length * 4) + 2;

  autoTable(doc, {
    ...tableStyles,
    startY: cursor,
    head: [model.summaryHead.map(pdfSafe)],
    body: pdfSafeRows(model.summaryBody.length
      ? model.summaryBody
      : [['—', 'No items match these filters', '', '', '', '']]),
  });

  model.sections.forEach((section) => {
    const heading = doc.splitTextToSize(pdfSafe(section.heading), 268);
    const headingHeight = (heading.length * 6) + 2;
    const pageHeight = doc.internal.pageSize.getHeight();
    let top = doc.lastAutoTable.finalY + 10;
    if (top + headingHeight > pageHeight - 20) {
      doc.addPage();
      top = 16;
    }
    doc.setFontSize(12);
    doc.setTextColor(0, 180, 230);
    doc.text(heading, 14, top);
    autoTable(doc, {
      ...tableStyles,
      startY: top + headingHeight,
      head: [section.head.map(pdfSafe)],
      body: pdfSafeRows(section.body),
      styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
    });
  });

  doc.save(filename);
}

export default function Library() {
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stations, setStations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  const [selectedCategoryType, setSelectedCategoryType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cueProblemsOnly, setCueProblemsOnly] = useState(false);
  const [uploadCategoryTypeFilter, setUploadCategoryTypeFilter] = useState('');
  const [bulkCategoryTypeFilter, setBulkCategoryTypeFilter] = useState('');
  const [editCategoryTypeFilter, setEditCategoryTypeFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    artist: '',
    album: '',
    category_ids: [],
    use_as_sweeper: false,
  });
  const [editingAsset, setEditingAsset] = useState(null);
  const [audioEditorOpen, setAudioEditorOpen] = useState(false);
  const [powerIntroEditorStation, setPowerIntroEditorStation] = useState(null);
  const [editingAudioAsset, setEditingAudioAsset] = useState(null);
  const [rekordboxInfoAsset, setRekordboxInfoAsset] = useState(null);
  const [rekordboxInfoOpen, setRekordboxInfoOpen] = useState(false);
  const audioEditorNavRef = useRef(0);
  const replaceAudioInputRef = useRef(null);
  const replaceAudioTargetRef = useRef(null);
  const [replaceAudioTarget, setReplaceAudioTarget] = useState(null);
  const [replacingAudio, setReplacingAudio] = useState(false);
  const [replaceAudioProgress, setReplaceAudioProgress] = useState(0);
  const replaceAudioProgressRef = useRef({ pct: 0, ts: 0 });
  const [replaceJobs, setReplaceJobs] = useState({});
  const replaceJobsRef = useRef({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [libraryColumnOrder, setLibraryColumnOrder] = useState(readLibraryColumnOrder);
  const [draggingColumn, setDraggingColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalAssets, setTotalAssets] = useState(0);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategoryIds, setBulkCategoryIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkUploadCategoryDialogOpen, setBulkUploadCategoryDialogOpen] = useState(false);
  const [bulkUploadStep, setBulkUploadStep] = useState(1); // 1 = pick categories, 2 = uploading
  const [uploadDoneToast, setUploadDoneToast] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [bulkUploadCategoryIds, setBulkUploadCategoryIds] = useState([]);
  const [uploadStationFilter, setUploadStationFilter] = useState('');
  const [uploadCategories, setUploadCategories] = useState([]);
  const [bulkUseAsSweeper, setBulkUseAsSweeper] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    album: '',
    category_ids: [],
    use_as_sweeper: false,
    restriction_enabled: false,
    restricted_days: [],
    restricted_start_time: '',
    restricted_end_time: '',
    active_date_enabled: false,
    active_start_date: '',
    active_end_date: '',
  });
  const [initialEditFormSnapshot, setInitialEditFormSnapshot] = useState(null);
  const audioRef = useRef(null);
  const [playingAssetId, setPlayingAssetId] = useState(null);

  // Upload queue panel state
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
  const [uploadPanelExpanded, setUploadPanelExpanded] = useState(true);
  const [uploadingActive, setUploadingActive] = useState(false);
  const uploadQueueIdRef = useRef(0);
  const uploadProgressSnapshotRef = useRef({});
  const uploadProgressTsRef = useRef({});
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const [dropboxImportDialogOpen, setDropboxImportDialogOpen] = useState(false);
  const [dropboxSyncs, setDropboxSyncs] = useState([]);
  const [selectedSyncForImport, setSelectedSyncForImport] = useState('');
  const [importingFromDropbox, setImportingFromDropbox] = useState(false);
  const [dropboxLinkDialogOpen, setDropboxLinkDialogOpen] = useState(false);
  const [linkingAsset, setLinkingAsset] = useState(null);

  // Remote Sync (URL / FTP / SFTP) state
  const [assetSyncConfig, setAssetSyncConfig] = useState(null);
  const [syncSectionOpen, setSyncSectionOpen] = useState(false);
  const [syncForm, setSyncForm] = useState({
    sync_type: 'url',
    enabled: true,
    schedule_cron: '0 0 * * *',
    url: '',
    ftp_host: '',
    ftp_port: '',
    ftp_username: '',
    ftp_password: '',
    ftp_remote_path: '',
    use_sftp: false,
    ignore_tls_errors: false,
  });
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncValidating, setSyncValidating] = useState(false);
  const [syncValidationResult, setSyncValidationResult] = useState(null);

  // Regional variations (when multi-region enabled)
  const [regions, setRegions] = useState([]);

  // Tab state: 0 = Library, 1 = Reports, 2 = Beatmix (if enabled)
  const [activeTab, setActiveTab] = useState(0);
  const [beatmixEnabled, setBeatmixEnabled] = useState(false);

  // Reports state
  const [reportGroupBy, setReportGroupBy] = useState('asset');
  const [reportFrom, setReportFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return formatLocalDate(d);
  });
  const [reportTo, setReportTo] = useState(formatLocalDate);
  const [reportStationId, setReportStationId] = useState('');
  const [reportCategoryTypeFilter, setReportCategoryTypeFilter] = useState('');
  const [reportCategoryId, setReportCategoryId] = useState('');
  const [reportCategories, setReportCategories] = useState([]);
  const [reportAssetId, setReportAssetId] = useState('');
  const [reportAssets, setReportAssets] = useState([]);
  const [reportSearch, setReportSearch] = useState('');
  const [reportDebouncedSearch, setReportDebouncedSearch] = useState('');
  const [reportTitleFilter, setReportTitleFilter] = useState('');
  const [reportDebouncedTitle, setReportDebouncedTitle] = useState('');
  const [reportArtistFilter, setReportArtistFilter] = useState('');
  const [reportDebouncedArtist, setReportDebouncedArtist] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [catalogueDownloading, setCatalogueDownloading] = useState(null);
  const [reportDetailAssetId, setReportDetailAssetId] = useState(null);
  const [reportDetailData, setReportDetailData] = useState(null);
  const [reportDetailLoading, setReportDetailLoading] = useState(false);
  const [reportView, setReportView] = useState('summary'); // 'summary' | 'log'
  const [logRows, setLogRows] = useState(null);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(0);
  const [logLoading, setLogLoading] = useState(false);
  const reportQueryGen = useRef(0);
  const playedAssetsGen = useRef(0);

  useEffect(() => {
    if (stations.length > 0) {
      if (selectedStation) {
        const station = stations.find(s => String(s.id) === String(selectedStation));
        setBeatmixEnabled(station?.beatmix_enabled || false);
      } else {
        setBeatmixEnabled(stations.some(s => s.beatmix_enabled));
      }
    } else {
      setBeatmixEnabled(false);
    }
  }, [stations, selectedStation]);

  useEffect(() => {
    if (!beatmixEnabled) setCueProblemsOnly(false);
  }, [beatmixEnabled]);

  const beatmixStationIds = useMemo(
    () => new Set(stations.filter((s) => s.beatmix_enabled).map((s) => String(s.id))),
    [stations]
  );

  // Mirrors the server rule: Beat Mixing tracks on Beat Mixing stations only.
  // `asset.rekordbox` is only sent for links made on Beat Mixing stations.
  const isBeatmixStationAsset = useCallback((asset) => {
    if (!asset) return false;
    if (selectedStation) return beatmixStationIds.has(String(selectedStation));
    if (asset.rekordbox) return true;
    if (asset.station_id != null) return beatmixStationIds.has(String(asset.station_id));
    return beatmixStationIds.size > 0;
  }, [selectedStation, beatmixStationIds]);

  const canShowRekordboxInfo = useCallback((asset) => (
    !!asset
    && !!(asset.is_mix_version || asset.has_mix_cues || asset.rekordbox)
    && isBeatmixStationAsset(asset)
  ), [isBeatmixStationAsset]);

  const openRekordboxInfo = useCallback((asset) => {
    setRekordboxInfoAsset(asset);
    setRekordboxInfoOpen(true);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LIBRARY_COLUMN_ORDER_KEY, JSON.stringify(libraryColumnOrder));
    } catch (_) {
      // Ignore storage access issues.
    }
  }, [libraryColumnOrder]);

  const dragColumns = useMemo(() => {
    const byKey = {
      title: {
        key: 'title',
        label: 'Title',
        sortKey: 'title',
        getValue: (asset) => asset.title || '-',
      },
      artist: {
        key: 'artist',
        label: 'Artist(s)',
        sortKey: 'artist',
        getValue: (asset) => asset.artist || '-',
      },
      categories: {
        key: 'categories',
        label: 'Categories',
        sortKey: 'categories',
        render: (asset) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {asset.categories && asset.categories.length > 0 ? (
              asset.categories.map((cat) => {
                const catColor = cat.color || '#60a5fa';
                return (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    size="small"
                    sx={{
                      bgcolor: `${catColor}1f`,
                      color: catColor,
                      border: `1px solid ${catColor}59`,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                );
              })
            ) : (
              <Typography variant="caption" color="text.secondary">-</Typography>
            )}
          </Box>
        ),
      },
    };
    return libraryColumnOrder.map((key) => byKey[key]).filter(Boolean);
  }, [libraryColumnOrder]);

  // The first title/artist column is the primary identity column, held in
  // stronger text; categories stay secondary wherever they are dropped.
  const primaryColumnKey = useMemo(() => (
    dragColumns.find((column) => column.key !== 'categories')?.key || null
  ), [dragColumns]);

  const columnOrderPreset = useMemo(() => {
    if (libraryColumnOrder.join(',') === LIBRARY_COLUMN_PRESETS[LIBRARY_COLUMN_ORDERS.ARTIST_TITLE].join(',')) {
      return LIBRARY_COLUMN_ORDERS.ARTIST_TITLE;
    }
    if (libraryColumnOrder.join(',') === LIBRARY_COLUMN_PRESETS[LIBRARY_COLUMN_ORDERS.TITLE_ARTIST].join(',')) {
      return LIBRARY_COLUMN_ORDERS.TITLE_ARTIST;
    }
    return LIBRARY_COLUMN_ORDERS.CUSTOM;
  }, [libraryColumnOrder]);

  const moveColumn = useCallback((fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setLibraryColumnOrder((current) => {
      const next = normalizeLibraryColumnOrder(current);
      const fromIndex = next.indexOf(fromKey);
      const toIndex = next.indexOf(toKey);
      if (fromIndex === -1 || toIndex === -1) return current;
      next.splice(fromIndex, 1);
      next.splice(toIndex, 0, fromKey);
      return next;
    });
  }, []);

  const handleColumnDragStart = useCallback((key) => (event) => {
    setDraggingColumn(key);
    setDragOverColumn(key);
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', key);
    } catch (_) {
      // Some browsers restrict dataTransfer writes; the drag state is enough.
    }
  }, []);

  const handleColumnDragOver = useCallback((key) => (event) => {
    if (!draggingColumn) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== key) setDragOverColumn(key);
  }, [draggingColumn, dragOverColumn]);

  const handleColumnDrop = useCallback((key) => (event) => {
    event.preventDefault();
    moveColumn(draggingColumn, key);
    setDraggingColumn(null);
    setDragOverColumn(null);
  }, [draggingColumn, moveColumn]);

  const handleColumnDragEnd = useCallback(() => {
    setDraggingColumn(null);
    setDragOverColumn(null);
  }, []);

  useEffect(() => {
    fetchStations();
    checkDropboxConnection();
    fetchDropboxSyncs();
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [selectedStation]);

  useEffect(() => {
    const fetchRegions = async () => {
      if (!selectedStation) {
        setRegions([]);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/regions`, {
          params: { station_id: selectedStation },
          withCredentials: true
        });
        setRegions((res.data || []).filter(r => r.is_active));
      } catch (error) {
        console.error('Error fetching regions:', error);
        setRegions([]);
      }
    };
    fetchRegions();
  }, [selectedStation]);

  useEffect(() => {
    fetchReportCategories();
  }, [reportStationId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page whenever filters, search, or sort direction change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStation, selectedCategoryType, selectedCategory, cueProblemsOnly, sortField, sortDirection]);

  useEffect(() => {
    fetchAssets();
  }, [debouncedSearchTerm, selectedStation, selectedCategoryType, selectedCategory, cueProblemsOnly, sortField, sortDirection, page, rowsPerPage]); // eslint-disable-line

  const libraryTabWasActive = useRef(true);
  useEffect(() => {
    if (activeTab === 0 && !libraryTabWasActive.current) {
      fetchAssets();
    }
    libraryTabWasActive.current = activeTab === 0;
  }, [activeTab]);

  useEffect(() => {
    if (!selectedCategory) return;
    const category = categories.find((cat) => String(cat.id) === String(selectedCategory));
    if (!category || (selectedCategoryType && getCategoryType(category) !== selectedCategoryType)) {
      setSelectedCategory('');
    }
  }, [categories, selectedCategory, selectedCategoryType]);

  useEffect(() => {
    if (!reportCategoryId) return;
    const category = reportCategories.find((cat) => String(cat.id) === String(reportCategoryId));
    if (!category || (reportCategoryTypeFilter && getCategoryType(category) !== reportCategoryTypeFilter)) {
      setReportCategoryId('');
    }
  }, [reportCategories, reportCategoryId, reportCategoryTypeFilter]);

  // Typing in the report filters should not fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setReportDebouncedSearch(reportSearch.trim()), REPORT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [reportSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setReportDebouncedTitle(reportTitleFilter.trim()), REPORT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [reportTitleFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setReportDebouncedArtist(reportArtistFilter.trim()), REPORT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [reportArtistFilter]);

  const uploadCategoriesById = useMemo(() => {
    const map = new Map();
    uploadCategories.forEach((category) => {
      map.set(category.id, category);
    });
    return map;
  }, [uploadCategories]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(category.id, category);
    });
    return map;
  }, [categories]);

  const categoryMenuProps = useMemo(() => ({
    PaperProps: {
      sx: {
        maxHeight: 360,
      },
    },
  }), []);

  const libraryFilterCategories = useMemo(
    () => filterCategoriesByType(categories, selectedCategoryType),
    [categories, selectedCategoryType]
  );

  const renderGroupedCategoryMenuItems = useCallback((categoryList, renderItem) => {
    const groups = groupCategoriesByType(categoryList);
    const showHeaders = groups.length > 1;
    return groups.flatMap((group) => {
      const items = group.categories.map((category) => renderItem(category));
      if (!showHeaders) return items;
      return [
        <ListSubheader
          key={`category-type-${group.type}`}
          disableSticky
          sx={{
            bgcolor: 'var(--rs-paper)',
            color: '#22d3ee',
            lineHeight: '32px',
            pointerEvents: 'none',
          }}
        >
          {group.label}
        </ListSubheader>,
        ...items,
      ];
    });
  }, []);

  const visibleSelectedFiles = useMemo(() => selectedFiles.slice(0, 50), [selectedFiles]);

  const selectedAssetsSet = useMemo(() => new Set(selectedAssets), [selectedAssets]);

  // Assets are already sorted by the server; expose them directly.
  const sortedAssets = assets;

  const normalizeLookupValue = useCallback((value) => {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }, []);

  const splitArtists = useCallback((value) => {
    if (!value) return [];
    const normalized = String(value).trim();
    if (!normalized) return [];
    return normalized
      .split(/\s+featuring\s+|\s+feat\.?\s+|\s+ft\.?\s+|\s+&\s+|\s+x\s+|\s+and\s+|\s*,\s*|\s+\+\s+/gi)
      .map((part) => part.trim())
      .filter(Boolean);
  }, []);

  const knownArtistsSet = useMemo(() => {
    const set = new Set();
    assets.forEach((asset) => {
      splitArtists(asset.artist).forEach((name) => set.add(normalizeLookupValue(name)));
      splitArtists(asset.featured_artists).forEach((name) => set.add(normalizeLookupValue(name)));
    });
    return set;
  }, [assets, normalizeLookupValue, splitArtists]);

  const looksLikeKnownArtist = useCallback((value) => {
    if (!value || knownArtistsSet.size === 0) return false;
    const normalized = normalizeLookupValue(value);
    if (!normalized) return false;
    if (knownArtistsSet.has(normalized)) return true;
    return splitArtists(value).some((name) => knownArtistsSet.has(normalizeLookupValue(name)));
  }, [knownArtistsSet, normalizeLookupValue, splitArtists]);

  const buildEditFormSnapshot = useCallback((source) => {
    const categoryIds = Array.isArray(source?.category_ids)
      ? source.category_ids.map((id) => String(id)).sort()
      : [];
    const restrictedDays = Array.isArray(source?.restricted_days)
      ? source.restricted_days.map((day) => String(day)).sort()
      : [];
    return {
      title: String(source?.title || '').trim(),
      artist: String(source?.artist || '').trim(),
      album: String(source?.album || '').trim(),
      category_ids: categoryIds,
      use_as_sweeper: !!source?.use_as_sweeper,
      restriction_enabled: !!source?.restriction_enabled,
      restricted_days: restrictedDays,
      restricted_start_time: String(source?.restricted_start_time || ''),
      restricted_end_time: String(source?.restricted_end_time || ''),
      active_date_enabled: !!source?.active_date_enabled,
      active_start_date: String(source?.active_start_date || ''),
      active_end_date: String(source?.active_end_date || ''),
    };
  }, []);

  const extractRestrictionFormValues = useCallback((asset) => {
    let restrictions = asset?.schedule_restrictions || {};
    if (typeof restrictions === 'string') {
      try {
        restrictions = JSON.parse(restrictions);
      } catch (_) {
        restrictions = {};
      }
    }
    const firstWindow = Array.isArray(restrictions?.windows) ? restrictions.windows[0] : null;
    const enabled = !!(restrictions?.enabled && firstWindow);
    const dateRange = (restrictions && typeof restrictions.active_date_range === 'object')
      ? restrictions.active_date_range
      : null;
    const dateRangeEnabled = !!(dateRange?.enabled && (dateRange?.start_date || dateRange?.end_date));
    return {
      restriction_enabled: enabled,
      restricted_days: Array.isArray(firstWindow?.days) ? firstWindow.days : [],
      restricted_start_time: firstWindow?.start_time || '',
      restricted_end_time: firstWindow?.end_time || '',
      active_date_enabled: dateRangeEnabled,
      active_start_date: dateRange?.start_date || '',
      active_end_date: dateRange?.end_date || '',
    };
  }, []);

  const currentEditFormSnapshot = useMemo(() => buildEditFormSnapshot(formData), [formData, buildEditFormSnapshot]);
  const isEditFormDirty = useMemo(() => {
    if (!initialEditFormSnapshot) return false;
    return JSON.stringify(currentEditFormSnapshot) !== JSON.stringify(initialEditFormSnapshot);
  }, [currentEditFormSnapshot, initialEditFormSnapshot]);

  const currentEditingIndex = useMemo(() => {
    if (!editingAsset) return -1;
    return sortedAssets.findIndex((asset) => String(asset.id) === String(editingAsset.id));
  }, [sortedAssets, editingAsset]);

  const currentEditingAudioIndex = useMemo(() => {
    if (!editingAudioAsset) return -1;
    return sortedAssets.findIndex((asset) => String(asset.id) === String(editingAudioAsset.id));
  }, [sortedAssets, editingAudioAsset]);

  const queueStats = useMemo(() => {
    let done = 0;
    let duplicates = 0;
    let failed = 0;

    uploadQueue.forEach((item) => {
      if (item.status === 'done') done += 1;
      else if (item.status === 'duplicate') duplicates += 1;
      else if (item.status === 'error') failed += 1;
    });

    const total = uploadQueue.length;
    const pending = total - done - duplicates - failed;
    const overallPct = total ? Math.round(((done + duplicates) / total) * 100) : 0;

    return { total, done, duplicates, failed, pending, overallPct };
  }, [uploadQueue]);

  const visibleUploadQueue = useMemo(() => uploadQueue.slice(0, 200), [uploadQueue]);

  const pendingAudioUploadCount = useMemo(() => (
    pendingUploadFiles.reduce((count, file) => (
      /\.(mp3|wav|flac|ogg|opus|m4a|aac)$/i.test(file.name) ? count + 1 : count
    ), 0)
  ), [pendingUploadFiles]);

  const checkDropboxConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/auth/status`, {
        withCredentials: true,
      });
      setDropboxConnected(response.data.connected);
    } catch (error) {
      console.error('Error checking Dropbox connection:', error);
      setDropboxConnected(false);
    }
  };

  const fetchDropboxSyncs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dropbox/syncs`, {
        withCredentials: true,
      });
      setDropboxSyncs(response.data || []);
    } catch (error) {
      console.error('Error fetching Dropbox syncs:', error);
    }
  };

  useEffect(() => {
    // Cleanup any playing preview when the component unmounts
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ArrowUpward sx={{ fontSize: 14, color: '#22d3ee' }} />
      : <ArrowDownward sx={{ fontSize: 14, color: '#22d3ee' }} />;
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedAssets(sortedAssets.map((asset) => asset.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const resolveUploadStationId = () => {
    // Empty string is an explicit "All Stations" choice in the upload dialog.
    // Do not fall back to the library filter, or that choice is ignored.
    if (uploadStationFilter !== '' && uploadStationFilter != null) {
      return uploadStationFilter;
    }
    if (stations.length === 1) return stations[0].id;
    return '';
  };

  const handleBulkUploadCategoryConfirm = () => {
    const files = pendingUploadFiles;
    const catIds = bulkUploadCategoryIds;
    const sweeper = bulkUseAsSweeper;
    const stationId = resolveUploadStationId();
    // Stay in the dialog but advance to the progress step
    setBulkUploadStep(2);
    startBatchUpload(files, catIds, sweeper, null, true /* inDialog */, stationId);
  };

  const handleCloseBulkUploadDialog = () => {
    if (uploadingActive) return;
    setBulkUploadCategoryDialogOpen(false);
    setBulkUploadStep(1);
    setPendingUploadFiles([]);
    setBulkUploadCategoryIds([]);
    setUploadStationFilter('');
    setUploadCategories([]);
    setUploadCategoryTypeFilter('');
    setBulkUseAsSweeper(false);
    setUploadQueue([]);
    setUploadPanelOpen(false);
  };

  const handleBulkAssignCategory = async () => {
    if (selectedAssets.length === 0 || bulkCategoryIds.length === 0) {
      alert('Please select assets and at least one category');
      return;
    }

    setBulkUpdating(true);
    try {
      // Update each asset with the selected categories
      // Use Promise.allSettled to handle individual failures
      const updatePromises = selectedAssets.map(assetId =>
        axios.put(
          `${API_URL}/api/assets/${assetId}`,
          { category_ids: bulkCategoryIds },
          { 
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            }
          }
        ).then((response) => {
          console.log(`Successfully updated asset ${assetId}:`, response.data);
          return { assetId, success: true };
        })
        .catch(error => {
          console.error(`Failed to update asset ${assetId}:`, error.response?.data || error.message);
          return { 
            assetId, 
            success: false, 
            error: error.response?.data?.message || error.response?.data?.error || error.message,
            status: error.response?.status
          };
        })
      );

      const results = await Promise.allSettled(updatePromises);
      
      // Process results
      const successful = [];
      const failed = [];
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          if (result.value.success) {
            successful.push(result.value.assetId);
          } else {
            failed.push({ assetId: result.value.assetId, error: result.value.error });
          }
        } else {
          failed.push({ assetId: selectedAssets[index], error: result.reason?.message || 'Unknown error' });
        }
      });
      
      // Refresh assets list
      await fetchAssets();
      
      // Show results
      if (failed.length === 0) {
        // All succeeded
        alert(`Successfully assigned ${bulkCategoryIds.length} categor${bulkCategoryIds.length === 1 ? 'y' : 'ies'} to ${successful.length} asset${successful.length === 1 ? '' : 's'}`);
        // Clear selection and close dialog
        setSelectedAssets([]);
        setBulkCategoryDialogOpen(false);
        setBulkCategoryIds([]);
        setBulkCategoryTypeFilter('');
      } else if (successful.length > 0) {
        // Partial success
        alert(`Partially completed: ${successful.length} asset${successful.length === 1 ? '' : 's'} updated, ${failed.length} failed. Check console for details.`);
        console.error('Failed assets:', failed);
        // Keep dialog open so user can retry
      } else {
        // All failed
        alert(`Failed to assign categories to all ${failed.length} asset${failed.length === 1 ? '' : 's'}. Check console for details.`);
        console.error('All updates failed:', failed);
      }
    } catch (error) {
      console.error('Error bulk assigning categories:', error);
      alert(`Failed to assign categories: ${error.message || 'Please try again.'}`);
    } finally {
      setBulkUpdating(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_field: sortField,
        sort_dir: sortDirection,
      };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (selectedStation) params.station_id = selectedStation;
      if (selectedCategory) params.category_id = selectedCategory;
      if (selectedCategoryType) params.category_type = selectedCategoryType;
      if (cueProblemsOnly) params.mix_cue_problems = 'true';

      const res = await axios.get(`${API_URL}/api/assets`, {
        params,
        withCredentials: true
      });

      const total = parseInt(res.headers?.['x-total-count'] ?? res.headers?.['X-Total-Count'] ?? '0', 10);
      setTotalAssets(isNaN(total) ? 0 : total);

      if (!res.data || !Array.isArray(res.data)) {
        setAssets([]);
        return;
      }

      setAssets(res.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load assets';
      alert(`Error loading assets: ${errorMsg}`);
    }
  };

  const filterCategoriesForStation = useCallback((categoryList, stationId) => {
    if (!stationId) return categoryList;
    const selected = String(stationId);

    return (categoryList || []).filter((category) => {
      const stationIds = Array.isArray(category.station_ids)
        ? category.station_ids.map((id) => String(id))
        : [];
      const legacyStationId = category.station_id == null ? '' : String(category.station_id);

      return legacyStationId === selected || stationIds.includes(selected);
    });
  }, []);

  const fetchCategories = async () => {
    try {
      const params = {};
      if (selectedStation) params.station_id = selectedStation;

      const res = await axios.get(`${API_URL}/api/categories`, {
        params,
        withCredentials: true
      });
      setCategories(filterCategoriesForStation(res.data || [], selectedStation));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchReportCategories = async () => {
    try {
      const params = {};
      if (reportStationId) params.station_id = reportStationId;

      const res = await axios.get(`${API_URL}/api/categories`, {
        params,
        withCredentials: true
      });
      setReportCategories(filterCategoriesForStation(res.data || [], reportStationId));
    } catch (error) {
      console.error('Error fetching report categories:', error);
      setReportCategories([]);
    }
  };

  const fetchReportAssets = useCallback(async () => {
    const gen = ++playedAssetsGen.current;
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStationId) params.station_id = reportStationId;
      if (reportCategoryId) params.category_id = reportCategoryId;
      if (reportDebouncedSearch) params.search = reportDebouncedSearch;
      if (reportDebouncedTitle) params.title = reportDebouncedTitle;
      if (reportDebouncedArtist) params.artist = reportDebouncedArtist;

      const res = await axios.get(`${API_URL}/api/playback-log/played-assets`, {
        params,
        withCredentials: true
      });
      if (gen !== playedAssetsGen.current) return;
      setReportAssets(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (gen !== playedAssetsGen.current) return;
      console.error('Error fetching report assets:', error);
    }
  }, [
    reportFrom, reportTo, reportStationId, reportCategoryId,
    reportDebouncedSearch, reportDebouncedTitle, reportDebouncedArtist,
  ]);

  const reportAssetLabelCache = useRef(new Map());

  const selectedReportAsset = useMemo(() => {
    if (!reportAssetId) return null;
    const key = String(reportAssetId);
    // Keep the label of a picked asset even after a search hides it from the
    // options list, so the picker never claims a chosen track "did not air".
    const known = reportAssets.find((asset) => String(asset.id) === key)
      || reportAssetLabelCache.current.get(key);
    if (known) return known;
    return {
      id: reportAssetId,
      title: 'Not aired in this period',
      artist: '',
      play_count: 0,
    };
  }, [reportAssets, reportAssetId]);

  const handleReportAssetChange = (asset) => {
    if (!asset) {
      setReportAssetId('');
      return;
    }
    reportAssetLabelCache.current.set(String(asset.id), asset);
    setReportAssetId(asset.id);
  };

  const reportAssetOptions = useMemo(() => {
    if (!selectedReportAsset) return reportAssets;
    if (reportAssets.some((asset) => String(asset.id) === String(selectedReportAsset.id))) {
      return reportAssets;
    }
    return [selectedReportAsset, ...reportAssets];
  }, [reportAssets, selectedReportAsset]);

  useEffect(() => {
    if (activeTab !== 1) return;
    fetchReportAssets();
  }, [fetchReportAssets, activeTab]);

  // Drop stale results as soon as filters change so Summary/PDF cannot
  // disagree with Play Log (they used to keep the previous unfiltered run).
  useEffect(() => {
    reportQueryGen.current += 1;
    setReportData(null);
    setLogRows(null);
    setLogTotal(0);
    setReportDetailAssetId(null);
    setReportDetailData(null);
  }, [
    reportFrom, reportTo, reportStationId, reportCategoryId, reportAssetId, reportGroupBy,
    reportDebouncedSearch, reportDebouncedTitle, reportDebouncedArtist,
  ]);

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      const list = res.data || [];
      setStations(list);
      if (list.length === 1 && !selectedStation) {
        setSelectedStation(list[0].id);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchUploadCategories = async (stationId = '') => {
    try {
      const params = {};
      if (stationId) params.station_id = stationId;

      const res = await axios.get(`${API_URL}/api/categories`, {
        params,
        withCredentials: true
      });
      setUploadCategories(res.data || []);
    } catch (error) {
      console.error('Error fetching upload categories:', error);
      setUploadCategories([]);
    }
  };

  // Helper function to extract metadata from filename
  const extractMetadataFromFilename = (filename) => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    
    // Try different patterns:
    // 1. "Artist - Title"
    // 2. "Artist - Title - Album"
    // 3. "Title" (if no dash)
    
    const parts = nameWithoutExt.split(/\s[-–—]\s/).map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      let candidateArtist = parts[0].trim();
      let candidateTitle = parts.slice(1).join(' - ').trim();
      const firstLooksLikeArtist = looksLikeKnownArtist(candidateArtist);
      const secondLooksLikeArtist = looksLikeKnownArtist(candidateTitle);
      if (secondLooksLikeArtist && !firstLooksLikeArtist) {
        // Handle common "Title - Artist" format.
        candidateArtist = candidateTitle;
        candidateTitle = parts[0].trim();
      }
      return {
        artist: candidateArtist,
        title: candidateTitle,
        album: parts.length >= 3 ? parts.slice(2).join(' - ').trim() : ''
      };
    } else {
      return {
        artist: '',
        title: nameWithoutExt.trim(),
        album: ''
      };
    }
  };

  // Helper function to check for and parse .mmd file
  const checkForMMDFile = async (audioFile) => {
    // Check if there's a corresponding .mmd file in the file list
    // For folder uploads, the .mmd file should be in the same directory
    // For now, we'll let the backend handle .mmd parsing since it has access to the file system
    // But we can try to find it in the file list if it was uploaded together
    
    // The backend will handle .mmd file detection automatically
    // This is just a placeholder for future frontend-side .mmd parsing if needed
    return null;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Separate audio files and .mmd files
    const audioFiles = files.filter(file => {
      const ext = file.name.toLowerCase().match(/\.[^.]*$/)?.[0];
      return /\.(mp3|wav|flac|ogg|opus|m4a|aac)$/i.test(ext);
    });

    const mmdFiles = files.filter(file => {
      return file.name.toLowerCase().endsWith('.mmd');
    });

    if (audioFiles.length === 0) {
      alert('No audio files selected. Please select MP3, WAV, FLAC, OGG/Opus, AAC, or M4A files.');
      event.target.value = '';
      return;
    }

    // IMPORTANT: Include both audio files AND .mmd files so backend can match them
    setSelectedFiles([...audioFiles, ...mmdFiles]);
    const initialUploadStation = selectedStation || (stations.length === 1 ? stations[0].id : '');
    setUploadStationFilter(initialUploadStation);
    setBulkUploadStep(1);
    setBulkUseAsSweeper(false);
    await fetchUploadCategories(initialUploadStation);
    
    // If single file, show dialog with pre-filled data
    if (audioFiles.length === 1) {
      const file = audioFiles[0];
      const filename = file.name.replace(/\.[^/.]+$/, '');
      
      // Extract metadata from filename
      const filenameMetadata = extractMetadataFromFilename(file.name);
      
      // Check if there's a corresponding .mmd file
      const correspondingMMD = mmdFiles.find(mmd => {
        const mmdBase = mmd.name.replace(/\.mmd$/i, '').replace(/\.(mp3|wav|flac|ogg|opus|m4a|aac)$/i, '');
        return mmdBase === filename;
      });
      
      let initialData = { ...filenameMetadata, category_ids: [], use_as_sweeper: false };
      
      // If .mmd file found, try to parse it
      if (correspondingMMD) {
        try {
          const formData = new FormData();
          formData.append('mmdFile', correspondingMMD);
          
          const response = await axios.post(`${API_URL}/api/assets/parse-mmd`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: true
          });
          
          if (response.data) {
            // Prefer .mmd data over filename parsing
            initialData = {
              title: response.data.title || filenameMetadata.title || '',
              artist: response.data.artist || filenameMetadata.artist || '',
              album: response.data.album || filenameMetadata.album || '',
              category_ids: [],
              use_as_sweeper: false,
            };
            console.log('Pre-filled form data from .mmd file:', initialData);
          }
        } catch (error) {
          console.warn('Could not parse .mmd file, using filename metadata:', error);
          // Fall back to filename metadata
        }
      }
      
      setUploadFormData(initialData);
      setUploadDialogOpen(true);
    } else {
      // Multiple files - show category selection dialog first
      setPendingUploadFiles([...audioFiles, ...mmdFiles]);
      setBulkUploadCategoryIds([]);
      setBulkUploadCategoryDialogOpen(true);
    }
    
    // Reset file input
    event.target.value = '';
  };

  const handleUploadStationFilterChange = async (stationId) => {
    setUploadStationFilter(stationId);
    setUploadFormData((prev) => ({ ...prev, category_ids: [] }));
    setBulkUploadCategoryIds([]);
    await fetchUploadCategories(stationId);
  };

  const handleFileUpload = () => {
    if (selectedFiles.length === 0) return;
    const files = selectedFiles;
    const metadata = {
      title: uploadFormData.title,
      artist: uploadFormData.artist,
      album: uploadFormData.album,
    };
    const catIds = uploadFormData.category_ids;
    const sweeper = uploadFormData.use_as_sweeper;
    const stationId = resolveUploadStationId();
    // Close dialog and hand off to queue
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setUploadFormData({ title: '', artist: '', album: '', category_ids: [], use_as_sweeper: false });
    startBatchUpload(files, catIds, sweeper, metadata, false, stationId);
  };


  const applyAssetToEditForm = (asset) => {
    const restrictionValues = extractRestrictionFormValues(asset);
    const nextFormData = {
      title: asset.title || '',
      artist: asset.artist || '',
      album: asset.album || '',
      category_ids: asset.categories?.map(c => c.id) || [],
      use_as_sweeper: !!(asset.is_sweeper || asset.play_as === 'sweeper'),
      ...restrictionValues,
    };
    setEditingAsset(asset);
    setFormData(nextFormData);
    setInitialEditFormSnapshot(buildEditFormSnapshot(nextFormData));
  };

  const handleEdit = async (asset) => {
    applyAssetToEditForm(asset);

    // Fetch Dropbox info if linked
    if (asset.dropbox) {
      try {
        const response = await axios.get(`${API_URL}/api/dropbox/assets/${asset.id}`, {
          withCredentials: true,
        });
        setFormData(prev => ({ ...prev, dropboxInfo: response.data }));
      } catch (error) {
        console.error('Error fetching Dropbox info:', error);
      }
    }

    // Fetch remote sync config
    try {
      const syncRes = await axios.get(`${API_URL}/api/asset-sync/${asset.id}`, {
        withCredentials: true,
      });
      const cfg = syncRes.data;
      setAssetSyncConfig(cfg);
      if (cfg) {
        setSyncForm({
          sync_type:        cfg.sync_type || 'url',
          enabled:          cfg.enabled !== false,
          schedule_cron:    cfg.schedule_cron || '0 0 * * *',
          url:              cfg.url || '',
          ftp_host:         cfg.ftp_host || '',
          ftp_port:         cfg.ftp_port || '',
          ftp_username:     cfg.ftp_username || '',
          ftp_password:     '',   // never pre-fill password
          ftp_remote_path:  cfg.ftp_remote_path || '',
          use_sftp:         !!cfg.use_sftp,
          ignore_tls_errors: !!cfg.ignore_tls_errors,
        });
        setSyncSectionOpen(true);
      } else {
        setSyncForm({
          sync_type: 'url', enabled: true, schedule_cron: '0 0 * * *',
          url: '', ftp_host: '', ftp_port: '', ftp_username: '',
          ftp_password: '', ftp_remote_path: '', use_sftp: false, ignore_tls_errors: false,
        });
        setSyncSectionOpen(false);
      }
    } catch {
      setAssetSyncConfig(null);
    }
    setSyncValidationResult(null);

    // Fetch power intros for all stations that have power_intro_enabled
    const piEnabledStations = stations.filter(s => {
      const settings = getStationSettings(s);
      return settings.power_intro_enabled;
    });
    if (piEnabledStations.length > 0) {
      const powerIntros = {};
      // Seed from the already-fetched power_intro for the selected station
      if (asset.power_intro && selectedStation) {
        powerIntros[selectedStation] = asset.power_intro;
      }
      await Promise.all(
        piEnabledStations
          .filter(s => String(s.id) !== String(selectedStation))
          .map(async (s) => {
            try {
              const piRes = await axios.get(
                `${API_URL}/api/assets/${asset.id}/power-intro?station_id=${s.id}`,
                { withCredentials: true }
              );
              if (piRes.data) powerIntros[s.id] = piRes.data;
            } catch { /* no power intro for this station */ }
          })
      );
      setEditingAsset(prev => prev ? { ...prev, power_intros: powerIntros } : prev);
    }

    setOpenDialog(true);
  };

  const handleSaveSyncConfig = async () => {
    if (!editingAsset) return;
    setSyncSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/asset-sync/${editingAsset.id}`,
        syncForm,
        { withCredentials: true },
      );
      setAssetSyncConfig(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save sync config');
    } finally {
      setSyncSaving(false);
    }
  };

  const handleDeleteSyncConfig = async () => {
    if (!editingAsset) return;
    if (!window.confirm('Remove the remote sync configuration for this asset?')) return;
    try {
      await axios.delete(`${API_URL}/api/asset-sync/${editingAsset.id}`, { withCredentials: true });
      setAssetSyncConfig(null);
      setSyncForm({
        sync_type: 'url', enabled: true, schedule_cron: '0 0 * * *',
        url: '', ftp_host: '', ftp_port: '', ftp_username: '',
        ftp_password: '', ftp_remote_path: '', use_sftp: false, ignore_tls_errors: false,
      });
      setSyncSectionOpen(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove sync config');
    }
  };

  const handleRunSyncNow = async () => {
    if (!editingAsset) return;
    setSyncRunning(true);
    try {
      await axios.post(`${API_URL}/api/asset-sync/${editingAsset.id}/run`, {}, { withCredentials: true });
      // Poll for completion
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await axios.get(`${API_URL}/api/asset-sync/${editingAsset.id}`, { withCredentials: true });
          const cfg = res.data;
          if (cfg && cfg.last_sync_status !== 'running') {
            setAssetSyncConfig(cfg);
            clearInterval(poll);
            setSyncRunning(false);
            fetchAssets();
          }
          if (attempts > 60) { clearInterval(poll); setSyncRunning(false); }
        } catch { clearInterval(poll); setSyncRunning(false); }
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start sync');
      setSyncRunning(false);
    }
  };

  const handleValidateSync = async () => {
    setSyncValidating(true);
    setSyncValidationResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/asset-sync/validate`,
        syncForm,
        { withCredentials: true },
      );
      setSyncValidationResult(res.data);
    } catch (err) {
      setSyncValidationResult({ ok: false, message: err.response?.data?.error || 'Request failed' });
    } finally {
      setSyncValidating(false);
    }
  };

  const handleNavigateEditAsset = useCallback((direction) => {
    if (!editingAsset || sortedAssets.length === 0) return;
    if (currentEditingIndex < 0) return;

    const nextIndex = currentEditingIndex + direction;
    if (nextIndex < 0 || nextIndex >= sortedAssets.length) return;

    const nextAsset = sortedAssets[nextIndex];
    if (!nextAsset) return;
    applyAssetToEditForm(nextAsset);
  }, [editingAsset, sortedAssets, currentEditingIndex, applyAssetToEditForm]);

  const openAudioEditorForAsset = async (asset) => {
    const nav = ++audioEditorNavRef.current;
    setEditingAudioAsset(asset);
    setAudioEditorOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/assets/${asset.id}`, {
        withCredentials: true,
      });
      if (nav !== audioEditorNavRef.current) return;
      setEditingAudioAsset(res.data);
    } catch (error) {
      if (nav !== audioEditorNavRef.current) return;
      console.error('Error fetching asset details:', error);
    }
  };

  const handleNavigateAudioAsset = useCallback((direction) => {
    if (currentEditingAudioIndex < 0) return;
    const targetIndex = currentEditingAudioIndex + direction;
    if (targetIndex < 0 || targetIndex >= sortedAssets.length) return;
    const targetAsset = sortedAssets[targetIndex];
    if (!targetAsset) return;
    openAudioEditorForAsset(targetAsset);
  }, [currentEditingAudioIndex, sortedAssets]);

  useEffect(() => {
    if (!openDialog) return undefined;

    const onKeyDown = (event) => {
      const activeTag = document.activeElement?.tagName;
      const isTypingField = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (isTypingField) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleNavigateEditAsset(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNavigateEditAsset(1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openDialog, handleNavigateEditAsset]);

  const handleImportFromDropbox = async () => {
    if (!selectedSyncForImport) {
      alert('Please select a sync configuration');
      return;
    }

    setImportingFromDropbox(true);
    try {
      await axios.post(
        `${API_URL}/api/dropbox/syncs/${selectedSyncForImport}/sync`,
        {},
        { withCredentials: true }
      );
      alert('Import started. Files will be imported shortly.');
      setDropboxImportDialogOpen(false);
      setSelectedSyncForImport('');
      // Refresh assets after a short delay
      setTimeout(() => {
        fetchAssets();
      }, 2000);
    } catch (error) {
      console.error('Error importing from Dropbox:', error);
      alert(error.response?.data?.message || 'Failed to import from Dropbox');
    } finally {
      setImportingFromDropbox(false);
    }
  };

  const handleSyncAsset = async (asset) => {
    if (!asset.dropbox) {
      alert('This asset is not linked to Dropbox');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/dropbox/assets/${asset.id}/sync`,
        {},
        { withCredentials: true }
      );
      alert('Asset synced successfully');
      fetchAssets();
    } catch (error) {
      console.error('Error syncing asset:', error);
      alert(error.response?.data?.message || 'Failed to sync asset');
    }
  };

  const handleLinkToDropbox = (asset) => {
    setLinkingAsset(asset);
    setDropboxLinkDialogOpen(true);
  };

  const handleUnlinkFromDropbox = async (asset) => {
    if (!window.confirm('Are you sure you want to unlink this asset from Dropbox?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/dropbox/assets/${asset.id}/link`, {
        withCredentials: true,
      });
      alert('Asset unlinked from Dropbox');
      fetchAssets();
    } catch (error) {
      console.error('Error unlinking asset:', error);
      alert(error.response?.data?.message || 'Failed to unlink asset');
    }
  };

  const handleSave = async () => {
    if (!editingAsset || !isEditFormDirty) return;
    try {
      const restrictionEnabled = !!formData.restriction_enabled;
      const restrictedDays = Array.isArray(formData.restricted_days)
        ? formData.restricted_days.map((day) => parseInt(day, 10)).filter((day) => !Number.isNaN(day))
        : [];
      const restrictedStart = String(formData.restricted_start_time || '');
      const restrictedEnd = String(formData.restricted_end_time || '');

      const activeDateEnabled = !!formData.active_date_enabled;
      const activeStartDate = String(formData.active_start_date || '');
      const activeEndDate = String(formData.active_end_date || '');
      const hasActiveDateRange = activeDateEnabled && (activeStartDate || activeEndDate);

      const scheduleRestrictions = (
        restrictionEnabled && restrictedStart && restrictedEnd
          ? {
            enabled: true,
            windows: [
              {
                days: restrictedDays,
                start_time: restrictedStart,
                end_time: restrictedEnd,
              }
            ],
          }
          : { enabled: false, windows: [] }
      );
      scheduleRestrictions.active_date_range = hasActiveDateRange
        ? {
          enabled: true,
          start_date: activeStartDate || null,
          end_date: activeEndDate || null,
        }
        : { enabled: false };

      const payload = {
        ...formData,
        is_sweeper: !!formData.use_as_sweeper,
        play_as: formData.use_as_sweeper ? 'sweeper' : 'normal',
        schedule_restrictions: scheduleRestrictions,
      };
      delete payload.use_as_sweeper;
      delete payload.restriction_enabled;
      delete payload.restricted_days;
      delete payload.restricted_start_time;
      delete payload.restricted_end_time;
      delete payload.active_date_enabled;
      delete payload.active_start_date;
      delete payload.active_end_date;
      await axios.put(
        `${API_URL}/api/assets/${editingAsset.id}`,
        payload,
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );
      setEditingAsset((prev) => prev ? ({ ...prev, ...payload }) : prev);
      setInitialEditFormSnapshot(buildEditFormSnapshot(formData));
      fetchAssets();
    } catch (error) {
      console.error('Error updating asset:', error);
      alert('Failed to update asset');
    }
  };

  const handleCloseEditDialog = () => {
    setOpenDialog(false);
    setEditingAsset(null);
    setInitialEditFormSnapshot(null);
    setAssetSyncConfig(null);
    setSyncSectionOpen(false);
    setSyncValidationResult(null);
    setSyncRunning(false);
    setEditCategoryTypeFilter('');
  };

  const handleDeleteClick = (asset) => {
    setAssetToDelete(asset);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assetToDelete) return;

    try {
      await axios.delete(`${API_URL}/api/assets/${assetToDelete.id}`, {
        withCredentials: true
      });
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Failed to delete asset: ' + (error.response?.data?.message || error.message));
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
    }
  };

  const stopCurrentPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setPlayingAssetId(null);
  };

  const handlePlayAsset = (asset) => {
    // If this asset is already playing, stop it
    if (playingAssetId === asset.id) {
      stopCurrentPreview();
      return;
    }

    // Stop any existing preview first
    stopCurrentPreview();

    const audioUrl = `${API_URL}/api/assets/${asset.id}/stream?t=${encodeURIComponent(asset.updated_at || asset.file_size || Date.now())}`;
    const audio = new Audio(audioUrl);

    audio.addEventListener('ended', () => {
      setPlayingAssetId(null);
      audioRef.current = null;
    });

    audio.addEventListener('error', () => {
      let errorMessage = 'Failed to play audio file.';
      if (audio.error) {
        switch (audio.error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Audio playback was aborted.';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading audio.';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Audio decoding error.';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Audio format not supported.';
            break;
          default:
            break;
        }
      }
      alert(errorMessage);
      setPlayingAssetId(null);
      audioRef.current = null;
    });

    audio
      .play()
      .then(() => {
        audioRef.current = audio;
        setPlayingAssetId(asset.id);
      })
      .catch((error) => {
        console.error('Error playing audio:', error);
        let errorMessage = 'Failed to play audio file: ' + error.message;
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Autoplay was blocked. Please click the play button again.';
        }
        alert(errorMessage);
        setPlayingAssetId(null);
        audioRef.current = null;
      });
  };

  // Upload a single queue item and track its individual progress
  const uploadQueueItem = async (item, categoryIds, useAsSweeper, metadata = {}, stationId = null) => {
    setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'uploading' } : q));
    uploadProgressSnapshotRef.current[item.id] = 0;
    uploadProgressTsRef.current[item.id] = 0;
    try {
      const fd = new FormData();
      fd.append('files', item.file);
      if (item.mmdFile) fd.append('files', item.mmdFile);
      if (stationId) fd.append('station_id', stationId);
      if (categoryIds?.length > 0) {
        categoryIds.forEach((id) => fd.append('category_ids', id));
      }
      if (metadata.title) fd.append('title', metadata.title);
      if (metadata.artist) fd.append('artist', metadata.artist);
      if (metadata.album) fd.append('album', metadata.album);
      fd.append('is_sweeper', useAsSweeper ? 'true' : 'false');
      fd.append('play_as', useAsSweeper ? 'sweeper' : 'normal');

      const response = await axios.post(`${API_URL}/api/assets`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 30 * 60 * 1000,
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          const lastPct = uploadProgressSnapshotRef.current[item.id] || 0;
          const now = Date.now();
          const lastTs = uploadProgressTsRef.current[item.id] || 0;

          if (pct === lastPct) return;
          if (pct < 100 && (pct - lastPct) < 2 && (now - lastTs) < 120) return;

          uploadProgressSnapshotRef.current[item.id] = pct;
          uploadProgressTsRef.current[item.id] = now;

          setUploadQueue(prev => prev.map(q => {
            if (q.id !== item.id || q.progress === pct) return q;
            return { ...q, progress: pct };
          }));
        },
      });
      // Check if the backend found a duplicate and reused the existing asset
      const wasDeduplicated = response.data?.results?.some?.(r => r.deduplicated)
        || response.data?.deduplicated;
      setUploadQueue(prev => prev.map(q =>
        q.id === item.id
          ? { ...q, status: wasDeduplicated ? 'duplicate' : 'done', progress: 100 }
          : q
      ));
      delete uploadProgressSnapshotRef.current[item.id];
      delete uploadProgressTsRef.current[item.id];
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed';
      setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error', error: msg } : q));
      delete uploadProgressSnapshotRef.current[item.id];
      delete uploadProgressTsRef.current[item.id];
    }
  };

  // Build a queue from files and process them 5 at a time.
  // inDialog=true means the dialog itself is showing the progress — skip the floating panel.
  const startBatchUpload = async (files, categoryIds, useAsSweeper, singleFileMetadata = null, inDialog = false, stationId = null) => {
    const audioFiles = files.filter(f => /\.(mp3|wav|flac|ogg|opus|m4a|aac)$/i.test(f.name));
    const mmdFiles  = files.filter(f => f.name.toLowerCase().endsWith('.mmd'));

    const queue = audioFiles.map((file) => {
      const baseName = file.name.replace(/\.[^.]+$/, '');
      const mmdFile = mmdFiles.find(m =>
        m.name.replace(/\.mmd$/i, '').replace(/\.(mp3|wav|flac|ogg|opus|m4a|aac)$/i, '') === baseName
      ) || null;
      const id = ++uploadQueueIdRef.current;
      return { id, name: file.name, file, mmdFile, status: 'queued', progress: 0, error: null };
    });

    setUploadQueue(queue);
    if (!inDialog) {
      setUploadPanelOpen(true);
      setUploadPanelExpanded(true);
    }
    setUploadingActive(true);

    const CONCURRENCY = 5;
    for (let i = 0; i < queue.length; i += CONCURRENCY) {
      await Promise.all(
        queue.slice(i, i + CONCURRENCY).map(item =>
          uploadQueueItem(item, categoryIds, useAsSweeper, singleFileMetadata || {}, stationId)
        )
      );
    }

    setUploadingActive(false);
    if (inDialog) setUploadDoneToast(true);
    fetchAssets();
    // Notify any open Settings page to refresh storage usage
    window.dispatchEvent(new CustomEvent('storage_usage_changed'));
  };

  const handleDownloadAsset = (asset) => {
    const link = document.createElement('a');
    link.href = `${API_URL}/api/assets/${asset.id}/download`;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStationSettings = (station) => {
    if (!station?.settings) return {};
    if (typeof station.settings === 'object') return station.settings;
    try { return JSON.parse(station.settings); } catch { return {}; }
  };

  const applyReplacedAsset = useCallback((updatedAsset) => {
    if (!updatedAsset?.id) return;
    setAssets((prev) => prev.map((asset) => (
      asset.id === updatedAsset.id
        ? {
          ...asset,
          ...updatedAsset,
          categories: updatedAsset.categories || asset.categories,
          dropbox: asset.dropbox,
          regional_variations: asset.regional_variations,
          power_intro: asset.power_intro,
        }
        : asset
    )));
    setEditingAsset((prev) => (
      prev && prev.id === updatedAsset.id
        ? { ...prev, ...updatedAsset }
        : prev
    ));
    setEditingAudioAsset((prev) => (
      prev && prev.id === updatedAsset.id
        ? { ...prev, ...updatedAsset }
        : prev
    ));
  }, []);

  // Declared before the poll effect so the poller always reads the committed
  // map rather than the snapshot it closed over.
  useEffect(() => {
    replaceJobsRef.current = replaceJobs;
  }, [replaceJobs]);

  const upsertReplaceJob = useCallback((assetId, patch) => {
    if (assetId == null) return;
    setReplaceJobs((prev) => {
      const current = replaceJobFor(prev, assetId) || { assetId };
      return { ...prev, [assetId]: { ...current, ...patch, assetId } };
    });
  }, []);

  const triggerReplaceAudio = (asset) => {
    if (!asset || replacingAudio) return;
    replaceAudioTargetRef.current = asset;
    setReplaceAudioTarget(asset);
    replaceAudioInputRef.current?.click();
  };

  const replacePollKey = activeReplacePollKey(replaceJobs);

  useEffect(() => {
    if (!replacePollKey) return undefined;
    let cancelled = false;
    let timer = null;
    const errors = {};
    const poll = async () => {
      const current = Object.values(replaceJobsRef.current || {})
        .filter((job) => job?.jobId && replaceAudioJobActive(job));
      if (!current.length) return;
      await Promise.all(current.map(async (job) => {
        const key = `${job.assetId}:${job.jobId}`;
        try {
          const res = await axios.get(`${API_URL}/api/assets/${job.assetId}/replace-audio`, {
            params: { job_id: job.jobId },
            withCredentials: true,
          });
          if (cancelled) return;
          errors[key] = 0;
          const data = res.data || {};
          setReplaceJobs((prev) => {
            const existing = replaceJobFor(prev, job.assetId);
            if (!existing || Number(existing.jobId) !== Number(data.id)) return prev;
            return {
              ...prev,
              [job.assetId]: {
                ...existing,
                status: data.status,
                error: data.error_message || null,
                duration_changed: data.duration_changed,
                previous_duration: data.previous_duration,
                duration: data.duration,
              },
            };
          });
          if (data.status === 'completed' && data.asset) {
            applyReplacedAsset(data.asset);
          }
        } catch (error) {
          if (cancelled) return;
          console.warn('Replace-audio status poll failed:', error);
          errors[key] = (errors[key] || 0) + 1;
          if (errors[key] >= REPLACE_AUDIO_POLL_MAX_ERRORS) {
            setReplaceJobs((prev) => {
              const existing = replaceJobFor(prev, job.assetId);
              if (!existing || Number(existing.jobId) !== Number(job.jobId)) return prev;
              return {
                ...prev,
                [job.assetId]: {
                  ...existing,
                  status: 'failed',
                  error: 'Lost track of the conversion. Refresh to see the latest state.',
                },
              };
            });
          }
        }
      }));
      if (!cancelled) timer = setTimeout(poll, REPLACE_AUDIO_POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [replacePollKey, applyReplacedAsset]);

  const handleReplaceAudioFile = async (event) => {
    const file = event.target.files?.[0];
    const asset = replaceAudioTargetRef.current || replaceAudioTarget || editingAsset;
    event.target.value = '';
    if (!file || !asset) return;
    setReplacingAudio(true);
    setReplaceAudioProgress(0);
    replaceAudioProgressRef.current = { pct: 0, ts: 0 };
    upsertReplaceJob(asset.id, {
      jobId: null,
      status: 'uploading',
      filename: file.name,
      error: null,
    });
    try {
      const payload = new FormData();
      payload.append('audioFile', file);
      const response = await axios.post(
        `${API_URL}/api/assets/${asset.id}/replace-audio`,
        payload,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30 * 60 * 1000,
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            const last = replaceAudioProgressRef.current;
            const now = Date.now();
            if (pct === last.pct) return;
            if (pct < 100 && (pct - last.pct) < 2 && (now - last.ts) < 120) return;
            replaceAudioProgressRef.current = { pct, ts: now };
            setReplaceAudioProgress(pct);
          },
        }
      );
      const updatedAsset = response.data?.asset;
      if (response.data?.converting && response.data?.job_id) {
        upsertReplaceJob(asset.id, {
          jobId: response.data.job_id,
          status: 'queued',
          filename: file.name,
          error: null,
        });
      } else {
        setReplaceJobs((prev) => {
          const next = { ...prev };
          delete next[asset.id];
          delete next[String(asset.id)];
          return next;
        });
        if (updatedAsset) applyReplacedAsset(updatedAsset);
      }
    } catch (error) {
      console.error('Error replacing asset audio:', error);
      upsertReplaceJob(asset.id, {
        jobId: null,
        status: 'failed',
        filename: file.name,
        error: error.response?.data?.message || error.message,
      });
    } finally {
      setReplacingAudio(false);
      setReplaceAudioProgress(0);
      replaceAudioProgressRef.current = { pct: 0, ts: 0 };
      setReplaceAudioTarget(null);
      replaceAudioTargetRef.current = null;
    }
  };

  const handlePowerIntroUpload = async (event, stationId) => {
    const file = event.target.files?.[0];
    if (!file || !editingAsset || !stationId) return;
    try {
      const formData = new FormData();
      formData.append('powerIntroFile', file);
      formData.append('station_id', stationId);
      await axios.post(
        `${API_URL}/api/assets/${editingAsset.id}/power-intro/upload`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const piRes = await axios.get(
        `${API_URL}/api/assets/${editingAsset.id}/power-intro?station_id=${stationId}`,
        { withCredentials: true }
      );
      setEditingAsset((prev) => prev ? {
        ...prev,
        power_intros: { ...(prev.power_intros || {}), [stationId]: piRes.data }
      } : prev);
    } catch (error) {
      console.error('Error uploading power intro:', error);
      alert('Failed to upload power intro: ' + (error.response?.data?.message || error.message));
    }
    event.target.value = '';
  };

  const handleSavePowerIntroMarkers = async (markers, stationId) => {
    if (!editingAsset) return;
    try {
      await axios.put(
        `${API_URL}/api/assets/${editingAsset.id}/power-intro/markers`,
        { station_id: stationId, markers },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );
      setEditingAsset((prev) => prev ? {
        ...prev,
        power_intros: {
          ...(prev.power_intros || {}),
          [stationId]: prev.power_intros?.[stationId]
            ? { ...prev.power_intros[stationId], markers }
            : prev.power_intros?.[stationId]
        }
      } : prev);
    } catch (error) {
      console.error('Error saving power intro markers:', error);
      alert('Failed to save power intro markers: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRegionalVariationUpload = async (event, regionId) => {
    const file = event.target.files?.[0];
    if (!file || !editingAsset || !selectedStation) return;
    try {
      const formData = new FormData();
      formData.append('regionalFile', file);
      formData.append('region_id', regionId);
      formData.append('station_id', selectedStation);
      await axios.post(
        `${API_URL}/api/assets/${editingAsset.id}/regional-variations/upload`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const varRes = await axios.get(
        `${API_URL}/api/assets/${editingAsset.id}/regional-variations?station_id=${selectedStation}`,
        { withCredentials: true }
      );
      setEditingAsset((prev) => prev ? { ...prev, regional_variations: varRes.data || [] } : prev);
      setAssets((prev) => prev.map(a => a.id === editingAsset.id ? { ...a, regional_variations: varRes.data || [] } : a));
    } catch (error) {
      console.error('Error uploading regional variation:', error);
      alert('Failed to upload regional version: ' + (error.response?.data?.message || error.message));
    }
    event.target.value = '';
  };

  const handleRegionalVariationDelete = async (regionId) => {
    if (!editingAsset) return;
    try {
      await axios.delete(
        `${API_URL}/api/assets/${editingAsset.id}/regional-variations/${regionId}`,
        { withCredentials: true }
      );
      setEditingAsset((prev) => prev ? {
        ...prev,
        regional_variations: (prev.regional_variations || []).filter(v => v.region_id !== regionId)
      } : prev);
      setAssets((prev) => prev.map(a => a.id === editingAsset.id
        ? { ...a, regional_variations: (a.regional_variations || []).filter(v => v.region_id !== regionId) }
        : a));
    } catch (error) {
      console.error('Error deleting regional variation:', error);
      alert('Failed to remove regional version');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoOrDate) => {
    if (!isoOrDate) return '—';
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return '—';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  };

  const formatSeconds = (sec) => {
    if (!sec || isNaN(sec)) return '0:00';
    const s = Math.round(parseFloat(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    return `${m}:${String(ss).padStart(2, '0')}`;
  };

  // Human-friendly gap between plays, e.g. "2d 4h", "3h 12m", "45m"
  const formatGap = (sec) => {
    if (sec == null || isNaN(sec)) return '—';
    const s = Math.round(parseFloat(sec));
    if (s < 60) return `${s}s`;
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const fetchReport = useCallback(async () => {
    const gen = reportQueryGen.current;
    setReportLoading(true);
    setReportData(null);
    try {
      const params = { group_by: reportGroupBy };
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStationId) params.station_id = reportStationId;
      if (reportCategoryId) params.category_id = reportCategoryId;
      if (reportAssetId) params.asset_id = reportAssetId;
      if (reportDebouncedSearch) params.search = reportDebouncedSearch;
      if (reportDebouncedTitle) params.title = reportDebouncedTitle;
      if (reportDebouncedArtist) params.artist = reportDebouncedArtist;
      const res = await axios.get(`${API_URL}/api/playback-log/report/summary`, { params, withCredentials: true });
      if (gen !== reportQueryGen.current) return;
      setReportData(res.data);
    } catch (err) {
      if (gen !== reportQueryGen.current) return;
      console.error('Error fetching report:', err);
      alert('Failed to load report: ' + (err.response?.data?.message || err.message));
    } finally {
      if (gen === reportQueryGen.current) setReportLoading(false);
    }
  }, [
    reportGroupBy, reportFrom, reportTo, reportStationId, reportCategoryId, reportAssetId,
    reportDebouncedSearch, reportDebouncedTitle, reportDebouncedArtist,
  ]);

  const fetchPlayLog = useCallback(async (page = 0) => {
    const gen = reportQueryGen.current;
    setLogLoading(true);
    try {
      const params = { page: page + 1, limit: 100 };
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStationId) params.station_id = reportStationId;
      if (reportCategoryId) params.category_id = reportCategoryId;
      if (reportAssetId) params.asset_id = reportAssetId;
      if (reportDebouncedSearch) params.search = reportDebouncedSearch;
      if (reportDebouncedTitle) params.title = reportDebouncedTitle;
      if (reportDebouncedArtist) params.artist = reportDebouncedArtist;
      const res = await axios.get(`${API_URL}/api/playback-log`, { params, withCredentials: true });
      if (gen !== reportQueryGen.current) return;
      setLogRows(res.data.data || []);
      setLogTotal(res.data.pagination?.total || 0);
      setLogPage(page);
    } catch (err) {
      if (gen !== reportQueryGen.current) return;
      console.error('Error fetching play log:', err);
      alert('Failed to load play log: ' + (err.response?.data?.message || err.message));
    } finally {
      if (gen === reportQueryGen.current) setLogLoading(false);
    }
  }, [
    reportFrom, reportTo, reportStationId, reportCategoryId, reportAssetId,
    reportDebouncedSearch, reportDebouncedTitle, reportDebouncedArtist,
  ]);

  const runReport = () => {
    fetchReport();
    fetchPlayLog(0);
  };

  const hasReportFilters = Boolean(
    reportSearch.trim() || reportTitleFilter.trim() || reportArtistFilter.trim()
    || reportAssetId || reportCategoryId
  );

  const clearReportFilters = () => {
    setReportSearch('');
    setReportTitleFilter('');
    setReportArtistFilter('');
    setReportAssetId('');
    setReportCategoryId('');
  };

  // One line describing what is narrowing the report, used above the results
  // so a filtered total is never mistaken for the whole period.
  const reportFilterSummary = [
    reportDebouncedSearch ? `“${reportDebouncedSearch}”` : '',
    reportDebouncedTitle ? `title “${reportDebouncedTitle}”` : '',
    reportDebouncedArtist ? `artist “${reportDebouncedArtist}”` : '',
    selectedReportAsset ? `asset: ${formatReportAssetLabel(selectedReportAsset)}` : '',
    reportCategoryId
      ? `category: ${reportCategories.find((c) => String(c.id) === String(reportCategoryId))?.name || reportCategoryId}`
      : '',
  ].filter(Boolean).join(' · ');

  useEffect(() => {
    if (activeTab !== 1) return;
    fetchReport();
    fetchPlayLog(0);
  }, [
    activeTab, reportFrom, reportTo, reportStationId, reportCategoryId, reportAssetId,
    reportDebouncedSearch, reportDebouncedTitle, reportDebouncedArtist,
    fetchReport, fetchPlayLog,
  ]);

  const handleReportViewChange = (view) => {
    setReportView(view);
    if (view === 'log' && logRows === null) fetchPlayLog(0);
    if (view === 'summary' && reportData === null) fetchReport();
  };

  const fetchAssetDetail = async (assetId) => {
    setReportDetailAssetId(assetId);
    setReportDetailLoading(true);
    setReportDetailData(null);
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStationId) params.station_id = reportStationId;
      const res = await axios.get(`${API_URL}/api/playback-log/asset/${assetId}`, { params, withCredentials: true });
      setReportDetailData(res.data);
    } catch (err) {
      console.error('Error fetching asset detail:', err);
    } finally {
      setReportDetailLoading(false);
    }
  };

  const playbackExportModel = () => buildPlaybackReportModel({
    groupBy: reportGroupBy,
    from: reportFrom,
    to: reportTo,
    rows: reportData?.rows || [],
    stats: reportData?.stats,
    filters: reportFilterSummary,
    formatDuration: formatSeconds,
    formatGap,
    formatWhen: (value) => (value ? new Date(value).toLocaleString() : ''),
  });

  const downloadReportPdf = () => {
    if (!reportData?.rows?.length) return;
    const model = playbackExportModel();
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.setTextColor(0, 180, 230);
    doc.text(pdfSafe(model.title), 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(pdfSafe(model.subtitle), 14, 25);
    if (model.statsLine) doc.text(pdfSafe(model.statsLine), 14, 31);
    autoTable(doc, {
      startY: 36,
      head: [model.headers.map(pdfSafe)],
      body: pdfSafeRows(model.body),
      theme: 'grid',
      headStyles: { fillColor: [0, 180, 230], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save(`playback-report-${reportGroupBy}-${formatLocalDate()}.pdf`);
  };

  const downloadReportExcel = async () => {
    if (!reportData?.rows?.length) return;
    const model = playbackExportModel();
    try {
      await saveExcelWorkbook(playbackWorkbook(model), {
        filename: `playback-report-${reportGroupBy}-${formatLocalDate()}.xlsx`,
        title: model.title,
        description: model.subtitle,
      });
    } catch (err) {
      console.error('Error downloading playback report:', err);
      alert(`Failed to download Excel report: ${err.message}`);
    }
  };

  const downloadLibraryCatalogue = async (format) => {
    const filterLabels = {
      categoryTypeLabel: CATEGORY_TYPE_LABELS[reportCategoryTypeFilter] || '',
      categoryName: reportCategories.find((category) => String(category.id) === String(reportCategoryId))?.name || '',
    };
    setCatalogueDownloading(format);
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStationId) params.station_id = reportStationId;
      if (reportCategoryId) params.category_id = reportCategoryId;
      if (reportCategoryTypeFilter) params.category_type = reportCategoryTypeFilter;
      if (reportDebouncedSearch) params.search = reportDebouncedSearch;
      if (reportDebouncedTitle) params.title = reportDebouncedTitle;
      if (reportDebouncedArtist) params.artist = reportDebouncedArtist;
      const res = await axios.get(`${API_URL}/api/assets/catalogue`, { params, withCredentials: true });
      const catalogue = res.data || {};
      const labels = { ...filterLabels, stationName: catalogueStationName(catalogue, stations, reportStationId) };
      const dateStr = formatLocalDate();
      if (format === 'pdf') {
        renderCataloguePdf(catalogue, labels, catalogueFilename(catalogue, 'pdf', dateStr));
      } else {
        await saveCatalogueExcel(catalogue, labels, catalogueFilename(catalogue, 'xlsx', dateStr));
      }
    } catch (err) {
      console.error('Error downloading library catalogue:', err);
      alert(`Failed to download library list: ${err.response?.data?.message || err.message}`);
    } finally {
      setCatalogueDownloading(null);
    }
  };

  const editingReplaceJob = replaceJobFor(replaceJobs, editingAsset?.id);
  const editingReplaceUploading = replacingAudio && editingReplaceJob?.status === 'uploading';

  return (
    <Box>
      <PageHeader
        overline="Media"
        title="Asset Library"
        subtitle={`${totalAssets.toLocaleString()} assets in your library`}
        icon={<MusicNote />}
      />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label="Library" icon={<MusicNote />} iconPosition="start" />
        <Tab label="Reports" icon={<ReportIcon />} iconPosition="start" />
        {beatmixEnabled && <Tab label="Beat Mixing" icon={<QueueMusic />} iconPosition="start" />}
      </Tabs>

      {/* ========================== LIBRARY TAB ========================== */}
      {activeTab === 0 && (<Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CloudSync />}
            onClick={() => {
              if (!dropboxConnected) {
                alert('Dropbox is not connected. Please connect Dropbox in Settings → Dropbox first.');
                return;
              }
              setDropboxImportDialogOpen(true);
            }}
            sx={{
              borderColor: dropboxConnected ? 'rgba(0,97,255,0.5)' : 'rgba(148,163,184,0.28)',
              color: dropboxConnected ? '#60a5fa' : 'text.disabled',
              '&:hover': {
                borderColor: dropboxConnected ? '#3b82f6' : 'rgba(148,163,184,0.28)',
                backgroundColor: dropboxConnected ? 'rgba(59,130,246,0.08)' : 'transparent',
              },
            }}
            title={dropboxConnected ? 'Import files from Dropbox' : 'Connect Dropbox in Settings first'}
          >
            Import from Dropbox
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
            sx={{
              borderColor: 'rgba(34,211,238,0.45)',
              color: '#22d3ee',
              '&:hover': { borderColor: '#22d3ee', backgroundColor: 'rgba(34,211,238,0.08)' },
            }}
          >
            Upload Folder
            <input
              type="file"
              hidden
              webkitdirectory="true"
              directory="true"
              multiple
              onClick={(event) => {
                // Allow selecting the same folder repeatedly.
                event.target.value = null;
              }}
              onChange={handleFileSelect}
            />
          </Button>
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUpload />}
            sx={{
              color: '#06222b',
              backgroundImage: 'linear-gradient(180deg, #4ee0f5 0%, #14b8d4 100%)',
              boxShadow: '0 6px 20px -8px rgba(34,211,238,0.55)',
              '&:hover': {
                backgroundImage: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)',
                boxShadow: '0 8px 26px -8px rgba(34,211,238,0.7)',
              },
            }}
          >
            Upload Files
            <input
              type="file"
              hidden
              accept="audio/*,.mmd"
              multiple
              onChange={handleFileSelect}
            />
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <TextField
          placeholder="Search by title, artist or album..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--rs-overlay-soft)',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
            },
          }}
        />
        {stations.length > 1 && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Station</InputLabel>
            <Select
              value={selectedStation}
              onChange={(e) => {
                setSelectedStation(e.target.value);
                const station = stations.find(s => String(s.id) === String(e.target.value));
                setBeatmixEnabled(station?.beatmix_enabled || false);
              }}
              label="Station"
            >
              <MenuItem value="">All Stations</MenuItem>
              {stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>
                  {station.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <CategoryTypeSelect
          value={selectedCategoryType}
          onChange={setSelectedCategoryType}
          sx={{ minWidth: 180 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
            MenuProps={categoryMenuProps}
          >
            <MenuItem value="">All Categories</MenuItem>
            {renderGroupedCategoryMenuItems(libraryFilterCategories, (cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {beatmixEnabled && (
          <Tooltip title="Only show tracks whose Rekordbox cue points run past the end of the audio file">
            <FormControlLabel
              control={(
                <Switch
                  checked={cueProblemsOnly}
                  onChange={(e) => setCueProblemsOnly(e.target.checked)}
                  color="error"
                />
              )}
              label="Cue problems"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', mx: 0 }}
            />
          </Tooltip>
        )}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Columns</InputLabel>
          <Select
            value={columnOrderPreset}
            onChange={(e) => {
              const next = e.target.value;
              if (next === LIBRARY_COLUMN_ORDERS.CUSTOM) return;
              setLibraryColumnOrder(normalizeLibraryColumnOrder(next));
            }}
            label="Columns"
          >
            <MenuItem value={LIBRARY_COLUMN_ORDERS.TITLE_ARTIST}>Title, Artist</MenuItem>
            <MenuItem value={LIBRARY_COLUMN_ORDERS.ARTIST_TITLE}>Artist, Title</MenuItem>
            {columnOrderPreset === LIBRARY_COLUMN_ORDERS.CUSTOM && (
              <MenuItem value={LIBRARY_COLUMN_ORDERS.CUSTOM} disabled>Custom order</MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedAssets.length > 0 && (
        <Card
          sx={{
            mb: 2,
            border: '1px solid rgba(34,211,238,0.35)',
            background: 'linear-gradient(135deg, rgba(34,211,238,0.10) 0%, rgba(59,130,246,0.06) 100%)',
            boxShadow: '0 10px 30px -18px rgba(34,211,238,0.5)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DoneAll sx={{ color: '#22d3ee' }} fontSize="small" />
              <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {selectedAssets.length} asset{selectedAssets.length === 1 ? '' : 's'} selected
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setBulkCategoryDialogOpen(true)}
              >
                Assign Category
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedAssets([])}
              >
                Clear Selection
              </Button>
            </Box>
          </Toolbar>
        </Card>
      )}

      <Card sx={{ overflow: 'hidden' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer>
            <Table sx={{ '& tbody tr:last-child td': { borderBottom: 'none' } }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedAssets.length > 0 && selectedAssets.length < sortedAssets.length}
                      checked={sortedAssets.length > 0 && selectedAssets.length === sortedAssets.length}
                      onChange={handleSelectAll}
                      sx={{ color: 'text.secondary', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#22d3ee' } }}
                    />
                  </TableCell>
                  {dragColumns.map((column) => {
                    const isDragging = draggingColumn === column.key;
                    const isDragOver = !!draggingColumn && dragOverColumn === column.key && !isDragging;
                    return (
                      <TableCell
                        key={column.key}
                        onDragOver={handleColumnDragOver(column.key)}
                        onDrop={handleColumnDrop(column.key)}
                        sx={{
                          opacity: isDragging ? 0.4 : 1,
                          boxShadow: isDragOver ? 'inset 2px 0 0 #22d3ee' : 'none',
                          bgcolor: isDragOver ? 'rgba(34,211,238,0.06)' : 'transparent',
                          transition: 'opacity 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                          '&:hover .library-column-drag-handle': { opacity: 0.75 },
                        }}
                      >
                        <Box
                          draggable
                          onDragStart={handleColumnDragStart(column.key)}
                          onDragEnd={handleColumnDragEnd}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'grab',
                            userSelect: 'none',
                            '&:active': { cursor: 'grabbing' },
                          }}
                        >
                          <Tooltip title="Drag to move this column" enterDelay={500}>
                            <DragIndicator
                              className="library-column-drag-handle"
                              sx={{ fontSize: 15, opacity: 0.28, transition: 'opacity 120ms ease' }}
                            />
                          </Tooltip>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              transition: 'color 120ms ease',
                              '&:hover': { color: 'var(--rs-text)' }
                            }}
                            onClick={() => handleSort(column.sortKey)}
                          >
                            {column.label}
                            <SortIcon field={column.sortKey} />
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'color 120ms ease',
                        '&:hover': { color: 'var(--rs-text)' }
                      }}
                      onClick={() => handleSort('duration')}
                    >
                      Duration
                      <SortIcon field="duration" />
                    </Box>
                  </TableCell>
                  
                  <TableCell>Dropbox</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAssets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                      <MusicNote sx={{ fontSize: 44, color: 'rgba(148,163,184,0.35)', mb: 1.5 }} />
                      <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 0.5 }}>
                        No assets found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters, or upload your first asset to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAssets.map((asset) => (
                    <TableRow 
                      key={asset.id}
                      selected={selectedAssetsSet.has(asset.id)}
                      sx={{
                        transition: 'background-color 120ms ease',
                        '&:hover': { bgcolor: 'rgba(148,163,184,0.06)' },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(34,211,238,0.08)',
                          boxShadow: 'inset 2px 0 0 #22d3ee',
                          '&:hover': { bgcolor: 'rgba(34,211,238,0.12)' },
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAssetsSet.has(asset.id)}
                          onChange={() => handleSelectAsset(asset.id)}
                          sx={{ color: 'text.secondary', '&.Mui-checked': { color: '#22d3ee' } }}
                        />
                      </TableCell>
                      {dragColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          sx={
                            column.key === primaryColumnKey
                              ? { fontWeight: 600, color: 'text.primary' }
                              : { color: 'text.secondary' }
                          }
                        >
                          {column.render ? column.render(asset) : column.getValue(asset)}
                        </TableCell>
                      ))}
                      <TableCell className="tabular-nums" sx={{ color: 'text.secondary' }}>{formatDuration(asset.duration)}</TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(asset.is_sweeper || asset.play_as === 'sweeper') && (
                            <Tooltip title="Plays over the intro of the next track">
                              <Chip
                                label="Sweeper"
                                size="small"
                                sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.35)', fontSize: '0.7rem', height: 22 }}
                              />
                            </Tooltip>
                          )}
                          {asset.dropbox && (
                            <Tooltip title={`Synced from Dropbox: ${asset.dropbox.dropboxPath}\nSync: ${asset.dropbox.syncName}\nLast synced: ${asset.dropbox.lastSyncedAt ? new Date(asset.dropbox.lastSyncedAt).toLocaleString() : 'Never'}`}>
                              <Chip
                                icon={<CloudSync sx={{ fontSize: 14 }} />}
                                label="Dropbox"
                                size="small"
                                sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.35)', fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { color: '#60a5fa' } }}
                              />
                            </Tooltip>
                          )}
                          {asset.regional_variations?.length > 0 && (
                            <Tooltip title={`${asset.regional_variations.length} regional version(s)`}>
                              <Chip
                                icon={<Public sx={{ fontSize: 14 }} />}
                                label={`${asset.regional_variations.length} region${asset.regional_variations.length > 1 ? 's' : ''}`}
                                size="small"
                                sx={{ bgcolor: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.35)', fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { color: '#4ade80' } }}
                              />
                            </Tooltip>
                          )}
                          {asset.has_mix_cues && isBeatmixStationAsset(asset) && (
                            <Tooltip title={`Mix cue points: In=${asset.mix_cue_points?.cue_in?.toFixed(1) || '0'}s, Done=${asset.mix_cue_points?.mix_done?.toFixed(1) || '0'}s, Outro=${asset.mix_cue_points?.outro_start?.toFixed(1) || '0'}s${asset.mix_bpm ? ` | ${asset.mix_bpm.toFixed(1)} BPM` : ''}${asset.mix_key ? ` | Key: ${asset.mix_key}` : ''}`}>
                              <Chip
                                icon={<QueueMusic sx={{ fontSize: 14 }} />}
                                label="Cue Points"
                                size="small"
                                onClick={canShowRekordboxInfo(asset) ? () => openRekordboxInfo(asset) : undefined}
                                sx={{ bgcolor: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.35)', fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { color: '#a78bfa' } }}
                              />
                            </Tooltip>
                          )}
                          {asset.mix_cue_problem && (
                            <Tooltip title={mixCueProblemTooltip(asset.mix_cue_problem)}>
                              {asset.mix_cue_problem.ignored ? (
                                <Chip
                                  icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                                  label="Cues past end"
                                  size="small"
                                  onClick={canShowRekordboxInfo(asset) ? () => openRekordboxInfo(asset) : undefined}
                                  sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)', fontSize: '0.7rem', height: 22, cursor: 'pointer', '& .MuiChip-icon': { color: '#f87171' } }}
                                />
                              ) : (
                                <Chip
                                  icon={<WarningAmber sx={{ fontSize: 14 }} />}
                                  label="Mark 4 past end"
                                  size="small"
                                  onClick={canShowRekordboxInfo(asset) ? () => openRekordboxInfo(asset) : undefined}
                                  sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.35)', fontSize: '0.7rem', height: 22, cursor: 'pointer', '& .MuiChip-icon': { color: '#fbbf24' } }}
                                />
                              )}
                            </Tooltip>
                          )}
                          {asset.markers_need_review && (
                            <Tooltip title="Audio length changed. Check cue, intro, and start-next markers.">
                              <Chip
                                icon={<WarningAmber sx={{ fontSize: 14 }} />}
                                label="Check markers"
                                size="small"
                                onClick={() => openAudioEditorForAsset(asset)}
                                sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.35)', fontSize: '0.7rem', height: 22, cursor: 'pointer', '& .MuiChip-icon': { color: '#fbbf24' } }}
                              />
                            </Tooltip>
                          )}
                          {asset.is_mix_version && isBeatmixStationAsset(asset) && (
                            <Tooltip title="Mix version — uploaded for beatmixing">
                              <Chip
                                label="Mix"
                                size="small"
                                onClick={canShowRekordboxInfo(asset) ? () => openRekordboxInfo(asset) : undefined}
                                sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.35)', fontSize: '0.7rem', height: 22 }}
                              />
                            </Tooltip>
                          )}
                          {!asset.dropbox && !asset.regional_variations?.length
                            && !((asset.has_mix_cues || asset.is_mix_version) && isBeatmixStationAsset(asset))
                            && !asset.markers_need_review && !asset.mix_cue_problem && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handlePlayAsset(asset)}
                            sx={{
                              color: playingAssetId === asset.id ? '#22d3ee' : 'text.secondary',
                              bgcolor: playingAssetId === asset.id ? 'rgba(34,211,238,0.12)' : 'transparent',
                              '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34,211,238,0.12)' },
                            }}
                            title={playingAssetId === asset.id ? 'Stop preview' : 'Play preview'}
                          >
                            {playingAssetId === asset.id ? (
                              <Pause fontSize="small" />
                            ) : (
                              <PlayArrow fontSize="small" />
                            )}
                          </IconButton>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadAsset(asset)}
                              sx={{ color: 'text.secondary', '&:hover': { color: '#4ade80', bgcolor: 'rgba(34,197,94,0.12)' } }}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={replaceAudioStatusTitle(replaceJobFor(replaceJobs, asset.id), replaceAudioProgress)}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => triggerReplaceAudio(asset)}
                                disabled={replacingAudio}
                                sx={{ color: 'text.secondary', '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34,211,238,0.12)' } }}
                              >
                                {replacingAudio && replaceJobFor(replaceJobs, asset.id)?.status === 'uploading'
                                  ? (
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                      <CircularProgress
                                        size={18}
                                        variant="determinate"
                                        value={replaceAudioProgress}
                                        sx={{ color: '#22d3ee' }}
                                      />
                                      <Box sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}>
                                        <Typography sx={{ fontSize: 7, lineHeight: 1, color: '#22d3ee', fontWeight: 700 }}>
                                          {replaceAudioProgress}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )
                                  : replaceAudioJobActive(replaceJobFor(replaceJobs, asset.id))
                                    ? <CircularProgress size={18} sx={{ color: '#22d3ee' }} />
                                  : <UploadFile fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(asset)}
                            sx={{ color: 'text.secondary', '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34,211,238,0.12)' } }}
                            title="Edit metadata"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openAudioEditorForAsset(asset)}
                            sx={{
                              color: asset.markers_need_review ? '#fbbf24' : 'text.secondary',
                              '&:hover': { color: '#4ade80', bgcolor: 'rgba(34,197,94,0.12)' },
                            }}
                            title={asset.markers_need_review ? 'Check audio markers (duration changed)' : 'Edit audio markers'}
                          >
                            <MusicNote fontSize="small" />
                          </IconButton>
                          {canShowRekordboxInfo(asset) && (
                            <IconButton
                              size="small"
                              onClick={() => openRekordboxInfo(asset)}
                              sx={{
                                color: asset.rekordbox ? '#a78bfa' : 'text.secondary',
                                '&:hover': { color: '#a78bfa', bgcolor: 'rgba(167,139,250,0.12)' },
                              }}
                              title={asset.rekordbox ? 'Rekordbox info' : 'Rekordbox info (not linked yet)'}
                            >
                              <Album fontSize="small" />
                            </IconButton>
                          )}
                          {asset.dropbox && (
                            <IconButton
                              size="small"
                              onClick={() => handleSyncAsset(asset)}
                              sx={{ color: 'text.secondary', '&:hover': { color: '#60a5fa', bgcolor: 'rgba(59,130,246,0.12)' } }}
                              title="Sync from Dropbox"
                            >
                              <CloudSync fontSize="small" />
                            </IconButton>
                          )}
                          {dropboxConnected && (
                            <IconButton
                              size="small"
                              onClick={() => asset.dropbox ? handleUnlinkFromDropbox(asset) : handleLinkToDropbox(asset)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': asset.dropbox
                                  ? { color: '#fbbf24', bgcolor: 'rgba(245,158,11,0.12)' }
                                  : { color: '#60a5fa', bgcolor: 'rgba(59,130,246,0.12)' },
                              }}
                              title={asset.dropbox ? 'Unlink from Dropbox' : 'Link to Dropbox'}
                            >
                              {asset.dropbox ? <LinkOff fontSize="small" /> : <LinkIcon fontSize="small" />}
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(asset)}
                            sx={{ color: 'text.secondary', '&:hover': { color: '#f87171', bgcolor: 'rgba(239,68,68,0.12)' } }}
                            title="Delete asset"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalAssets}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[50, 100, 250]}
            sx={{ borderTop: '1px solid', borderColor: 'divider', color: 'text.secondary' }}
          />
        </CardContent>
      </Card>

      {/* Edit Asset Dialog */}
      <Dialog open={openDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Edit Asset</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleNavigateEditAsset(-1)}
                disabled={currentEditingIndex <= 0}
                title="Previous asset"
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleNavigateEditAsset(1)}
                disabled={currentEditingIndex < 0 || currentEditingIndex >= sortedAssets.length - 1}
                title="Next asset"
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Artist(s)"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                helperText="Separate multiple artists with: Feat, Featuring, &, X, or comma"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album"
                value={formData.album}
                onChange={(e) => setFormData({ ...formData, album: e.target.value })}
              />
            </Grid>
            {categories.length > CATEGORY_TYPE_FILTER_THRESHOLD && (
              <Grid item xs={12}>
                <CategoryTypeSelect
                  value={editCategoryTypeFilter}
                  onChange={setEditCategoryTypeFilter}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  value={formData.category_ids}
                  onChange={(e) => setFormData({ ...formData, category_ids: e.target.value })}
                  label="Categories"
                  MenuProps={categoryMenuProps}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const cat = categoriesById.get(id);
                        return cat ? (
                          <Chip key={id} label={cat.name} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {renderGroupedCategoryMenuItems(
                    filterCategoriesByType(categories, editCategoryTypeFilter),
                    (cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.use_as_sweeper}
                    onChange={(e) => setFormData({ ...formData, use_as_sweeper: e.target.checked })}
                  />
                }
                label={
                  <Tooltip title="Sweepers play over the intro of the next track (when it has an intro set). Dry audio like jingles or IDs.">
                    <span>Use as sweeper</span>
                  </Tooltip>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.restriction_enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        restriction_enabled: e.target.checked,
                      })}
                    />
                  }
                  label="Restrict this asset by day/time"
                />
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                  When active, this asset is excluded from scheduling during the selected days and times.
                </Typography>
                {formData.restriction_enabled && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Restricted Days</InputLabel>
                        <Select
                          multiple
                          value={formData.restricted_days || []}
                          label="Restricted Days"
                          onChange={(e) => setFormData({ ...formData, restricted_days: e.target.value })}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((dayValue) => {
                                const dayOption = WEEKDAY_OPTIONS.find((d) => d.value === Number(dayValue));
                                return (
                                  <Chip key={dayValue} size="small" label={dayOption?.label || dayValue} />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {WEEKDAY_OPTIONS.map((dayOption) => (
                            <MenuItem key={dayOption.value} value={dayOption.value}>
                              {dayOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        value={formData.restricted_start_time || ''}
                        onChange={(e) => setFormData({ ...formData, restricted_start_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        value={formData.restricted_end_time || ''}
                        onChange={(e) => setFormData({ ...formData, restricted_end_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.active_date_enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        active_date_enabled: e.target.checked,
                      })}
                    />
                  }
                  label="Limit availability by date range"
                />
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                  When active, this asset is only scheduled between the dates below. Leave a date blank to leave that end open.
                </Typography>
                {formData.active_date_enabled && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={formData.active_start_date || ''}
                        onChange={(e) => setFormData({ ...formData, active_start_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={formData.active_end_date || ''}
                        onChange={(e) => setFormData({ ...formData, active_end_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: formData.active_start_date || undefined }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 0.5 }}>
                  Replace Audio
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                  Upload a new master file for this asset. Conversion continues in the background after the upload finishes, so you can keep working.
                </Typography>
                {editingAsset?.duration != null && (
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    Current duration: {formatDuration(editingAsset.duration)}
                    {editingAsset.filename ? ` · ${editingAsset.filename}` : ''}
                  </Typography>
                )}
                {editingAsset?.markers_need_review && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1.5, bgcolor: 'rgba(245,158,11,0.08)', color: '#fbbf24', '& .MuiAlert-icon': { color: '#fbbf24' } }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          openAudioEditorForAsset(editingAsset);
                        }}
                      >
                        Check markers
                      </Button>
                    }
                  >
                    Audio length changed. Please check cue, intro, and start-next markers.
                  </Alert>
                )}
                {replaceAudioJobActive(editingReplaceJob) && (
                  <Alert severity="info" sx={{ mb: 1.5, bgcolor: 'rgba(34,211,238,0.08)', color: '#67e8f9', '& .MuiAlert-icon': { color: '#22d3ee' } }}>
                    Audio uploaded. Converting in the background — you can keep editing or replace another file. The new audio will be used when conversion finishes.
                  </Alert>
                )}
                {editingReplaceJob?.status === 'failed' && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    Replace audio failed{editingReplaceJob.error ? `: ${editingReplaceJob.error}` : '.'}
                  </Alert>
                )}
                {editingReplaceJob?.status === 'superseded' && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    This upload was replaced by a newer one for the same asset, so it was discarded.
                  </Alert>
                )}
                {editingReplaceJob?.status === 'completed' && editingReplaceJob.duration_changed && (
                  <Alert severity="success" sx={{ mb: 1.5 }}>
                    New audio is ready. Duration changed from {formatDuration(editingReplaceJob.previous_duration)} to {formatDuration(editingReplaceJob.duration)}. Please check the file markers.
                  </Alert>
                )}
                {editingReplaceJob?.status === 'completed' && !editingReplaceJob.duration_changed && (
                  <Alert severity="success" sx={{ mb: 1.5 }}>
                    New audio is ready and in use.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={editingReplaceUploading ? (
                        <CircularProgress
                          size={14}
                          variant="determinate"
                          value={replaceAudioProgress}
                          sx={{ color: '#22d3ee' }}
                        />
                      ) : replaceAudioJobActive(editingReplaceJob) ? (
                        <CircularProgress size={14} sx={{ color: '#22d3ee' }} />
                      ) : <UploadFile />}
                      disabled={replacingAudio}
                      onClick={() => triggerReplaceAudio(editingAsset)}
                      sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
                    >
                      {editingReplaceUploading
                        ? `Uploading ${replaceAudioProgress}%`
                        : (editingReplaceJob?.status === 'queued'
                          ? 'Queued for conversion'
                          : (replaceAudioJobActive(editingReplaceJob)
                            ? 'Converting in background'
                            : 'Upload new audio'))}
                    </Button>
                  </Box>
                  {editingReplaceUploading && (
                    <LinearProgress
                      variant="determinate"
                      value={replaceAudioProgress}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: 'rgba(34,211,238,0.12)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee' },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Power Intro Upload and Marker Editing (per station) */}
            {stations.filter(s => getStationSettings(s).power_intro_enabled).map(station => (
              <Grid item xs={12} key={station.id}>
                <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)', mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                    Power Intro — {station.name} (Optional)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                    Upload a power intro version for this track. This will be used for {station.name} only.
                  </Typography>
                  {editingAsset?.power_intros?.[station.id] && (
                    <Typography variant="caption" sx={{ color: '#22c55e', display: 'block', mb: 1 }}>
                      ✓ Power intro uploaded
                    </Typography>
                  )}
                  <input
                    type="file"
                    accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac,audio/mpeg,audio/wav,audio/flac,audio/ogg,audio/opus,audio/mp4,audio/aac"
                    style={{ display: 'block', marginBottom: 12 }}
                    onChange={(e) => handlePowerIntroUpload(e, station.id)}
                  />
                  {editingAsset?.power_intros?.[station.id] && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#22d3ee', color: '#22d3ee', mb: 1 }}
                        onClick={() => setPowerIntroEditorStation(station.id)}
                      >
                        Edit Markers
                      </Button>
                      <AudioEditor
                        asset={{
                          id: editingAsset.id,
                          title: `${editingAsset.title} (${station.name} Power Intro)`,
                          duration: editingAsset.power_intros[station.id]?.duration || editingAsset.duration || 0,
                          filepath: editingAsset.power_intros[station.id]?.filepath || editingAsset.power_intros[station.id]?.file_path,
                          markers: editingAsset.power_intros[station.id]?.markers || null,
                        }}
                        isOpen={powerIntroEditorStation === station.id}
                        onClose={() => setPowerIntroEditorStation(null)}
                        saveDirectly={false}
                        onSave={(markers) => handleSavePowerIntroMarkers(markers, station.id)}
                      />
                    </>
                  )}
                </Box>
              </Grid>
            ))}

            {/* Regional versions (when multi-region enabled) */}
            {regions.length > 0 && selectedStation && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)', mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Public fontSize="small" /> Regional versions
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                    Add region-specific audio (e.g. local news). When this asset plays in a region, the regional version is used instead.
                  </Typography>
                  {regions.map((region) => {
                    const variation = (editingAsset?.regional_variations || []).find(v => v.region_id === region.id);
                    return (
                      <Box
                        key={region.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          p: 1,
                          bgcolor: 'rgba(255,255,255,0.04)',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 120, color: '#ccc' }}>
                          {region.name}
                        </Typography>
                        {variation?.filepath ? (
                          <>
                            <Typography variant="caption" sx={{ color: '#888', flex: 1 }}>
                              {variation.filepath}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CloudUpload />}
                              onClick={() => document.getElementById(`regional-upload-${region.id}`)?.click()}
                              sx={{ borderColor: '#0061ff', color: '#0061ff' }}
                            >
                              Replace
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleRegionalVariationDelete(region.id)}
                              sx={{ color: '#ff5252' }}
                              title="Remove regional version"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            onClick={() => document.getElementById(`regional-upload-${region.id}`)?.click()}
                            sx={{ borderColor: '#0061ff', color: '#0061ff', flex: 1, justifyContent: 'flex-start' }}
                          >
                            Add audio for {region.name}
                          </Button>
                        )}
                        <input
                          id={`regional-upload-${region.id}`}
                          type="file"
                          accept="audio/*"
                          style={{ display: 'none' }}
                          onChange={(e) => handleRegionalVariationUpload(e, region.id)}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            )}
            {editingAsset?.dropbox && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)', mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                    Dropbox Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 0.5 }}>
                    <strong>File:</strong> {editingAsset.dropbox.fileName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 0.5 }}>
                    <strong>Path:</strong> {editingAsset.dropbox.dropboxPath}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 0.5 }}>
                    <strong>Sync:</strong> {editingAsset.dropbox.syncName}
                  </Typography>
                  {editingAsset.dropbox.lastSyncedAt && (
                    <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                      <strong>Last Synced:</strong> {new Date(editingAsset.dropbox.lastSyncedAt).toLocaleString()}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudSync />}
                      onClick={async () => {
                        await handleSyncAsset(editingAsset);
                        handleCloseEditDialog();
                      }}
                      sx={{ borderColor: '#0061ff', color: '#0061ff' }}
                    >
                      Sync Now
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkOff />}
                      onClick={async () => {
                        await handleUnlinkFromDropbox(editingAsset);
                        handleCloseEditDialog();
                      }}
                      sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                    >
                      Unlink
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
            {dropboxConnected && !editingAsset?.dropbox && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)', mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                    Link to Dropbox
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                    Link this asset to a file in Dropbox to enable automatic syncing.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    onClick={() => {
                      handleCloseEditDialog();
                      handleLinkToDropbox(editingAsset);
                    }}
                    sx={{ borderColor: '#0061ff', color: '#0061ff' }}
                  >
                    Link to Dropbox File
                  </Button>
                </Box>
              </Grid>
            )}

            {/* ── Remote Sync (URL / FTP / SFTP) ── */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)', mt: 2 }}>
                {/* Header / toggle */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: syncSectionOpen ? 2 : 0 }}
                  onClick={() => setSyncSectionOpen(v => !v)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule fontSize="small" sx={{ color: '#22d3ee' }} />
                    <Typography variant="subtitle2" sx={{ color: '#22d3ee' }}>
                      Remote Sync
                    </Typography>
                    {assetSyncConfig && (
                      <Chip
                        size="small"
                        label={assetSyncConfig.enabled ? (assetSyncConfig.sync_type?.toUpperCase()) : 'Disabled'}
                        sx={{
                          bgcolor: assetSyncConfig.enabled ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.08)',
                          color: assetSyncConfig.enabled ? '#22d3ee' : '#888',
                          fontWeight: 600,
                          fontSize: '0.68rem',
                        }}
                      />
                    )}
                  </Box>
                  <IconButton size="small" sx={{ color: '#888' }}>
                    {syncSectionOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Box>

                <Collapse in={syncSectionOpen}>
                  {/* Last sync status */}
                  {assetSyncConfig && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {assetSyncConfig.last_sync_status === 'success' && <CheckCircleOutline fontSize="small" sx={{ color: '#22c55e' }} />}
                        {assetSyncConfig.last_sync_status === 'error'   && <SyncErrorIcon fontSize="small" sx={{ color: '#ef4444' }} />}
                        {assetSyncConfig.last_sync_status === 'running' && <CircularProgress size={14} sx={{ color: '#f59e0b' }} />}
                        {(!assetSyncConfig.last_sync_status || assetSyncConfig.last_sync_status === 'pending') && <Schedule fontSize="small" sx={{ color: '#888' }} />}
                        <Typography variant="caption" sx={{ color: '#ccc' }}>
                          {assetSyncConfig.last_sync_status === 'running' && 'Syncing…'}
                          {assetSyncConfig.last_sync_status === 'success' && `Last synced: ${assetSyncConfig.last_synced_at ? new Date(assetSyncConfig.last_synced_at).toLocaleString() : 'Unknown'}`}
                          {assetSyncConfig.last_sync_status === 'error'   && `Error: ${assetSyncConfig.last_sync_error || 'Unknown error'}`}
                          {(!assetSyncConfig.last_sync_status || assetSyncConfig.last_sync_status === 'pending') && 'Not yet synced'}
                        </Typography>
                      </Box>
                      {assetSyncConfig.next_sync_at && (
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Next: {new Date(assetSyncConfig.next_sync_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    {/* Type + enabled */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#888' }}>Source Type</InputLabel>
                        <Select
                          value={syncForm.sync_type}
                          label="Source Type"
                          onChange={e => setSyncForm(f => ({ ...f, sync_type: e.target.value }))}
                          sx={{ color: 'var(--rs-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                        >
                          <MenuItem value="url">URL (HTTP / HTTPS)</MenuItem>
                          <MenuItem value="ftp">FTP</MenuItem>
                          <MenuItem value="sftp">SFTP (SSH)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#888' }}>Schedule</InputLabel>
                        <Select
                          value={['0 * * * *','0 */6 * * *','0 */12 * * *','0 0 * * *','0 0 * * 0'].includes(syncForm.schedule_cron) ? syncForm.schedule_cron : 'custom'}
                          label="Schedule"
                          onChange={e => {
                            if (e.target.value !== 'custom') setSyncForm(f => ({ ...f, schedule_cron: e.target.value }));
                          }}
                          sx={{ color: 'var(--rs-text)', '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                        >
                          <MenuItem value="0 * * * *">Every hour</MenuItem>
                          <MenuItem value="0 */6 * * *">Every 6 hours</MenuItem>
                          <MenuItem value="0 */12 * * *">Every 12 hours</MenuItem>
                          <MenuItem value="0 0 * * *">Daily (midnight)</MenuItem>
                          <MenuItem value="0 0 * * 0">Weekly (Sunday)</MenuItem>
                          <MenuItem value="custom">Custom cron…</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Custom cron expression */}
                    {!['0 * * * *','0 */6 * * *','0 */12 * * *','0 0 * * *','0 0 * * 0'].includes(syncForm.schedule_cron) && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small"
                          label="Cron expression"
                          placeholder="e.g. 30 4 * * 1-5"
                          value={syncForm.schedule_cron}
                          onChange={e => setSyncForm(f => ({ ...f, schedule_cron: e.target.value }))}
                          InputLabelProps={{ style: { color: '#888' } }}
                          inputProps={{ style: { color: 'var(--rs-text)' } }}
                          sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          helperText="Standard 5-part cron: min hour dom month dow"
                          FormHelperTextProps={{ style: { color: '#666' } }}
                        />
                      </Grid>
                    )}

                    {/* URL fields */}
                    {syncForm.sync_type === 'url' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small"
                          label="Audio URL"
                          placeholder="https://example.com/audio/jingle.mp3"
                          value={syncForm.url}
                          onChange={e => setSyncForm(f => ({ ...f, url: e.target.value }))}
                          InputLabelProps={{ style: { color: '#888' } }}
                          inputProps={{ style: { color: 'var(--rs-text)' } }}
                          sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                        />
                      </Grid>
                    )}

                    {/* FTP / SFTP fields */}
                    {(syncForm.sync_type === 'ftp' || syncForm.sync_type === 'sftp') && (
                      <>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth size="small"
                            label="Host"
                            placeholder="ftp.example.com"
                            value={syncForm.ftp_host}
                            onChange={e => setSyncForm(f => ({ ...f, ftp_host: e.target.value }))}
                            InputLabelProps={{ style: { color: '#888' } }}
                            inputProps={{ style: { color: 'var(--rs-text)' } }}
                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth size="small"
                            label="Port"
                            placeholder={syncForm.sync_type === 'sftp' ? '22' : '21'}
                            value={syncForm.ftp_port}
                            onChange={e => setSyncForm(f => ({ ...f, ftp_port: e.target.value }))}
                            InputLabelProps={{ style: { color: '#888' } }}
                            inputProps={{ style: { color: 'var(--rs-text)', inputMode: 'numeric' } }}
                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small"
                            label="Username"
                            value={syncForm.ftp_username}
                            onChange={e => setSyncForm(f => ({ ...f, ftp_username: e.target.value }))}
                            InputLabelProps={{ style: { color: '#888' } }}
                            inputProps={{ style: { color: 'var(--rs-text)' } }}
                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small"
                            label={assetSyncConfig ? 'Password (leave blank to keep existing)' : 'Password'}
                            type="password"
                            value={syncForm.ftp_password}
                            onChange={e => setSyncForm(f => ({ ...f, ftp_password: e.target.value }))}
                            InputLabelProps={{ style: { color: '#888' } }}
                            inputProps={{ style: { color: 'var(--rs-text)' } }}
                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small"
                            label="Remote file path"
                            placeholder="/audio/jingles/jingle.mp3"
                            value={syncForm.ftp_remote_path}
                            onChange={e => setSyncForm(f => ({ ...f, ftp_remote_path: e.target.value }))}
                            InputLabelProps={{ style: { color: '#888' } }}
                            inputProps={{ style: { color: 'var(--rs-text)' } }}
                            sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: '#3a4456' } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={syncForm.ignore_tls_errors}
                                onChange={e => setSyncForm(f => ({ ...f, ignore_tls_errors: e.target.checked }))}
                                sx={{ color: '#666', '&.Mui-checked': { color: '#22d3ee' } }}
                              />
                            }
                            label={<Typography variant="body2" sx={{ color: '#ccc' }}>Ignore TLS/certificate errors</Typography>}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Enabled toggle */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={syncForm.enabled}
                            onChange={e => setSyncForm(f => ({ ...f, enabled: e.target.checked }))}
                            sx={{ color: '#666', '&.Mui-checked': { color: '#22d3ee' } }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ color: '#ccc' }}>Enable scheduled sync</Typography>}
                      />
                    </Grid>

                    {/* Validation result */}
                    {syncValidationResult && (
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 1.5, borderRadius: 1,
                          bgcolor: syncValidationResult.ok ? 'rgba(76,175,80,0.1)' : 'rgba(244,67,54,0.1)',
                          border: `1px solid ${syncValidationResult.ok ? '#22c55e' : '#ef4444'}`,
                        }}>
                          <Typography variant="caption" sx={{ color: syncValidationResult.ok ? '#22c55e' : '#ef4444' }}>
                            {syncValidationResult.ok ? '✓ ' : '✗ '}{syncValidationResult.message}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Action buttons */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small" variant="outlined"
                          onClick={handleValidateSync}
                          disabled={syncValidating}
                          startIcon={syncValidating ? <CircularProgress size={14} /> : <Dns fontSize="small" />}
                          sx={{ borderColor: '#888', color: '#ccc' }}
                        >
                          Test Connection
                        </Button>
                        <Button
                          size="small" variant="contained"
                          onClick={handleSaveSyncConfig}
                          disabled={syncSaving}
                          startIcon={syncSaving ? <CircularProgress size={14} /> : <Storage fontSize="small" />}
                          sx={{ bgcolor: '#22d3ee', color: '#000', '&:hover': { bgcolor: '#00b8d9' } }}
                        >
                          Save
                        </Button>
                        {assetSyncConfig && (
                          <>
                            <Button
                              size="small" variant="outlined"
                              onClick={handleRunSyncNow}
                              disabled={syncRunning || assetSyncConfig?.last_sync_status === 'running'}
                              startIcon={syncRunning ? <CircularProgress size={14} /> : <PlayCircleOutline fontSize="small" />}
                              sx={{ borderColor: '#22c55e', color: '#22c55e' }}
                            >
                              {syncRunning ? 'Syncing…' : 'Sync Now'}
                            </Button>
                            <Button
                              size="small" variant="outlined"
                              onClick={handleDeleteSyncConfig}
                              startIcon={<DeleteIcon fontSize="small" />}
                              sx={{ borderColor: '#ef4444', color: '#ef4444' }}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Close</Button>
          <Button onClick={handleSave} disabled={!isEditFormDirty} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
            {isEditFormDirty ? 'Save' : 'Saved'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Asset Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        setUploadStationFilter('');
        setUploadCategories([]);
        setUploadCategoryTypeFilter('');
        setUploadFormData({
          title: '',
          artist: '',
          album: '',
          category_ids: [],
          use_as_sweeper: false,
        });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Assets` : 'Asset'}
        </DialogTitle>
        <DialogContent>
          {selectedFiles.length > 1 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'var(--rs-paper)', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected files ({selectedFiles.length}):
              </Typography>
              <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                {visibleSelectedFiles.map((file, index) => (
                  <Typography key={index} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    • {file.name}
                  </Typography>
                ))}
                {selectedFiles.length > visibleSelectedFiles.length && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                    +{selectedFiles.length - visibleSelectedFiles.length} more files
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              {selectedFiles.length === 1 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  File: {selectedFiles[0]?.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title *"
                value={uploadFormData.title}
                onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Artist(s)"
                value={uploadFormData.artist}
                onChange={(e) => setUploadFormData({ ...uploadFormData, artist: e.target.value })}
                helperText="Separate multiple artists with: Feat, Featuring, &, X, or comma"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Album"
                value={uploadFormData.album}
                onChange={(e) => setUploadFormData({ ...uploadFormData, album: e.target.value })}
              />
            </Grid>
            {stations.length > 1 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={uploadStationFilter}
                    onChange={(e) => handleUploadStationFilterChange(e.target.value)}
                    label="Station"
                  >
                    <MenuItem value="">All Stations</MenuItem>
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {uploadCategories.length > CATEGORY_TYPE_FILTER_THRESHOLD && (
              <Grid item xs={12}>
                <CategoryTypeSelect
                  value={uploadCategoryTypeFilter}
                  onChange={setUploadCategoryTypeFilter}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Categories</InputLabel>
                <Select
                  multiple
                  value={uploadFormData.category_ids}
                  onChange={(e) => setUploadFormData({ ...uploadFormData, category_ids: e.target.value })}
                  label="Categories"
                  MenuProps={categoryMenuProps}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const cat = uploadCategoriesById.get(id);
                        return cat ? (
                          <Chip key={id} label={cat.name} size="small" />
                        ) : null;
                      })}
                    </Box>
                  )}
                >
                  {renderGroupedCategoryMenuItems(
                    filterCategoriesByType(uploadCategories, uploadCategoryTypeFilter),
                    (cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!uploadFormData.use_as_sweeper}
                    onChange={(e) => setUploadFormData({ ...uploadFormData, use_as_sweeper: e.target.checked })}
                  />
                }
                label={
                  <Tooltip title="Sweepers play over the intro of the next track (when it has an intro set). Dry audio like jingles or IDs.">
                    <span>Use as sweeper</span>
                  </Tooltip>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFiles([]);
            setUploadStationFilter('');
            setUploadCategories([]);
            setUploadCategoryTypeFilter('');
            setUploadFormData({
              title: '',
              artist: '',
              album: '',
              category_ids: [],
              use_as_sweeper: false,
            });
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload} 
            variant="contained" 
            sx={{ bgcolor: '#22d3ee' }}
            disabled={!uploadFormData.title || selectedFiles.length === 0}
          >
            {`Upload ${selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'File'}`}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Audio Editor Dialog */}
        <input
          ref={replaceAudioInputRef}
          type="file"
          accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac,audio/mpeg,audio/wav,audio/flac,audio/ogg,audio/opus,audio/mp4,audio/aac"
          style={{ display: 'none' }}
          onChange={handleReplaceAudioFile}
        />
        <AudioEditor
          asset={editingAudioAsset}
          isOpen={audioEditorOpen}
          hasPrevious={currentEditingAudioIndex > 0}
          hasNext={currentEditingAudioIndex >= 0 && currentEditingAudioIndex < sortedAssets.length - 1}
          preloadAssets={[
            sortedAssets[currentEditingAudioIndex + 1],
            sortedAssets[currentEditingAudioIndex + 2],
          ].filter(Boolean)}
          onPrevious={() => handleNavigateAudioAsset(-1)}
          onNext={() => handleNavigateAudioAsset(1)}
          onClose={() => {
            setAudioEditorOpen(false);
            setEditingAudioAsset(null);
          }}
          onSave={() => {
            fetchAssets();
            setEditingAsset((prev) => (prev ? { ...prev, markers_need_review: false } : prev));
            setEditingAudioAsset((prev) => (prev ? { ...prev, markers_need_review: false } : prev));
          }}
        />

        <RekordboxInfoDialog
          open={rekordboxInfoOpen && !!rekordboxInfoAsset}
          asset={rekordboxInfoAsset}
          stationId={selectedStation || undefined}
          onClose={() => setRekordboxInfoOpen(false)}
          onOpenBeatmix={beatmixEnabled ? () => {
            setRekordboxInfoOpen(false);
            setActiveTab(2);
          } : undefined}
        />

        {/* Import from Dropbox Dialog */}
        <Dialog
          open={dropboxImportDialogOpen}
          onClose={() => {
            setDropboxImportDialogOpen(false);
            setSelectedSyncForImport('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Import from Dropbox</DialogTitle>
          <DialogContent>
            {!dropboxConnected ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Dropbox is not connected.</strong>
                </Typography>
                <Typography variant="body2">
                  Please connect your Dropbox account in Settings → Dropbox before importing files.
                </Typography>
              </Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Select a sync configuration to import files from Dropbox into the asset library.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Sync Configuration</InputLabel>
                  <Select
                    value={selectedSyncForImport}
                    label="Sync Configuration"
                    onChange={(e) => setSelectedSyncForImport(e.target.value)}
                  >
                    {dropboxSyncs.filter(sync => sync.enabled).map((sync) => (
                      <MenuItem key={sync.id} value={sync.id}>
                        {sync.name} - {sync.dropbox_path}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {dropboxSyncs.filter(sync => sync.enabled).length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No enabled sync configurations found. Create one in Settings → Dropbox.
                  </Alert>
                )}
              </>
            )}
            {dropboxConnected && dropboxSyncs.filter(sync => sync.enabled).length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid var(--rs-border)' }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  <strong>Sync Existing Files:</strong> Match existing assets in your library with files in Dropbox by filename.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API_URL}/api/dropbox/sync-existing`,
                        { syncId: selectedSyncForImport },
                        { withCredentials: true }
                      );
                      alert('Existing files synced successfully. Check the Dropbox column to see linked assets.');
                      fetchAssets();
                    } catch (error) {
                      console.error('Error syncing existing files:', error);
                      alert(error.response?.data?.message || 'Failed to sync existing files');
                    }
                  }}
                  disabled={!selectedSyncForImport || importingFromDropbox}
                  sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
                >
                  Sync Existing Assets
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDropboxImportDialogOpen(false);
              setSelectedSyncForImport('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleImportFromDropbox}
              variant="contained"
              disabled={!dropboxConnected || !selectedSyncForImport || importingFromDropbox}
              sx={{ bgcolor: '#0061ff' }}
            >
              {importingFromDropbox ? 'Importing...' : 'Import New Files'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Link Asset to Dropbox Dialog */}
        <Dialog
          open={dropboxLinkDialogOpen}
          onClose={() => {
            setDropboxLinkDialogOpen(false);
            setLinkingAsset(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Link Asset to Dropbox File</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Select a sync configuration and browse to find the Dropbox file to link to "{linkingAsset?.title}".
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sync Configuration</InputLabel>
              <Select
                value={selectedSyncForImport}
                label="Sync Configuration"
                onChange={(e) => setSelectedSyncForImport(e.target.value)}
              >
                {dropboxSyncs.filter(sync => sync.enabled).map((sync) => (
                  <MenuItem key={sync.id} value={sync.id}>
                    {sync.name} - {sync.dropbox_path}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                After selecting a sync configuration, you'll be able to browse Dropbox files and link this asset.
                The asset will then sync automatically when the Dropbox file is updated.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDropboxLinkDialogOpen(false);
              setLinkingAsset(null);
              setSelectedSyncForImport('');
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedSyncForImport}
              sx={{ bgcolor: '#0061ff' }}
              onClick={() => {
                // TODO: Implement file browser for linking
                alert('File browser for linking will be implemented. For now, use the sync configuration to automatically link files.');
                setDropboxLinkDialogOpen(false);
                setLinkingAsset(null);
                setSelectedSyncForImport('');
              }}
            >
              Browse Dropbox Files
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setAssetToDelete(null);
          }}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{assetToDelete?.title}" by {assetToDelete?.artist || 'Unknown Artist'}?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              This action cannot be undone. The file will be permanently deleted from the server.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDeleteDialogOpen(false);
              setAssetToDelete(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Upload Dialog — Step 1: pick categories  |  Step 2: upload progress */}
        {(() => {
          const qTotal = queueStats.total;
          const qDone = queueStats.done;
          const qDuplicates = queueStats.duplicates;
          const qFailed = queueStats.failed;
          const qPending = queueStats.pending;
          const overallPct = queueStats.overallPct;

          return (
            <Dialog
              open={bulkUploadCategoryDialogOpen}
              onClose={handleCloseBulkUploadDialog}
              maxWidth="sm"
              fullWidth
              disableEscapeKeyDown={uploadingActive}
            >
              {/* ── Step 1: Category selection ── */}
              {bulkUploadStep === 1 && (
                <>
                  <DialogTitle>
                    Upload {pendingAudioUploadCount} File{pendingAudioUploadCount === 1 ? '' : 's'}
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Optionally assign categories to all files before uploading.
                    </Typography>
                    {stations.length > 1 && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Station</InputLabel>
                        <Select
                          value={uploadStationFilter}
                          onChange={(e) => handleUploadStationFilterChange(e.target.value)}
                          label="Station"
                        >
                          <MenuItem value="">All Stations</MenuItem>
                          {stations.map((station) => (
                            <MenuItem key={station.id} value={station.id}>
                              {station.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {uploadCategories.length > CATEGORY_TYPE_FILTER_THRESHOLD && (
                      <CategoryTypeSelect
                        value={uploadCategoryTypeFilter}
                        onChange={setUploadCategoryTypeFilter}
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                    )}
                    <FormControl fullWidth>
                      <InputLabel>Categories</InputLabel>
                      <Select
                        multiple
                        value={bulkUploadCategoryIds}
                        onChange={(e) => setBulkUploadCategoryIds(e.target.value)}
                        label="Categories"
                        MenuProps={categoryMenuProps}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((id) => {
                              const category = uploadCategoriesById.get(id);
                              return (
                                <Chip
                                  key={id}
                                  label={category?.name || id}
                                  size="small"
                                  sx={{ bgcolor: category?.color || '#1976D2', color: '#fff' }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {renderGroupedCategoryMenuItems(
                          filterCategoriesByType(uploadCategories, uploadCategoryTypeFilter),
                          (category) => (
                            <MenuItem key={category.id} value={category.id}>
                              <Checkbox checked={bulkUploadCategoryIds.includes(category.id)} />
                              <Chip
                                label={category.name}
                                size="small"
                                sx={{ bgcolor: category.color || '#1976D2', color: '#fff', ml: 1 }}
                              />
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bulkUseAsSweeper}
                          onChange={(e) => setBulkUseAsSweeper(e.target.checked)}
                        />
                      }
                      label={
                        <Tooltip title="Sweepers play over the intro of the next track. Use for dry audio like jingles or IDs.">
                          <span>Use as sweeper (all files)</span>
                        </Tooltip>
                      }
                      sx={{ mt: 2, display: 'block' }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseBulkUploadDialog}>Cancel</Button>
                    <Button
                      onClick={handleBulkUploadCategoryConfirm}
                      variant="contained"
                      startIcon={<CloudUpload />}
                      sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                    >
                      Start Upload
                    </Button>
                  </DialogActions>
                </>
              )}

              {/* ── Step 2: Upload progress ── */}
              {bulkUploadStep === 2 && (
                <>
                  <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {uploadingActive
                        ? <CircularProgress size={20} sx={{ color: '#22d3ee' }} />
                        : qFailed > 0
                          ? <ErrorOutline sx={{ color: '#ef4444' }} />
                          : <DoneAll sx={{ color: '#22c55e' }} />
                      }
                      <Box>
                        <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                          {uploadingActive
                            ? `Uploading… ${qDone + qDuplicates} of ${qTotal}`
                            : qFailed > 0
                              ? `Finished with ${qFailed} error${qFailed > 1 ? 's' : ''}`
                              : 'Upload complete'
                          }
                        </Typography>
                        {uploadingActive && (
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {qPending} remaining · {qDone + qDuplicates} done
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </DialogTitle>

                  {/* Overall progress bar */}
                  <LinearProgress
                    variant="determinate"
                    value={overallPct}
                    sx={{
                      height: 4,
                      bgcolor: 'var(--rs-border)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: qFailed > 0 ? '#ef4444' : '#22d3ee',
                      },
                    }}
                  />

                  <DialogContent sx={{ p: 0 }}>
                    {/* Per-file list */}
                    <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                      {visibleUploadQueue.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            px: 2.5, py: 1.25,
                            borderBottom: '1px solid var(--rs-surface)',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            bgcolor: item.status === 'error'     ? 'rgba(244,67,54,0.06)'
                                   : item.status === 'duplicate' ? 'rgba(255,193,7,0.05)'
                                   : 'transparent',
                          }}
                        >
                          {/* Status icon */}
                          <Box sx={{ flexShrink: 0, width: 20, display: 'flex', justifyContent: 'center' }}>
                            {item.status === 'done'      && <CheckCircle sx={{ fontSize: 18, color: '#22c55e' }} />}
                            {item.status === 'duplicate' && (
                              <Tooltip title="Already in library – added to selected category">
                                <LibraryAdd sx={{ fontSize: 18, color: '#fbbf24' }} />
                              </Tooltip>
                            )}
                            {item.status === 'error'     && (
                              <Tooltip title={item.error}>
                                <ErrorOutline sx={{ fontSize: 18, color: '#ef4444' }} />
                              </Tooltip>
                            )}
                            {item.status === 'uploading' && <CircularProgress size={16} sx={{ color: '#22d3ee' }} />}
                            {item.status === 'queued'    && <HourglassEmpty sx={{ fontSize: 18, color: '#5b6675' }} />}
                          </Box>

                          {/* Filename + per-file bar */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              title={item.name}
                              sx={{
                                color: item.status === 'error'     ? '#ef4444'
                                     : item.status === 'duplicate' ? '#fbbf24'
                                     : item.status === 'done'      ? 'var(--rs-muted)'
                                     : 'var(--rs-text)',
                              }}
                            >
                              {item.name}
                            </Typography>
                            {item.status === 'uploading' && (
                              <LinearProgress
                                variant="determinate"
                                value={item.progress}
                                sx={{ mt: 0.5, height: 3, bgcolor: 'var(--rs-border)', '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee' } }}
                              />
                            )}
                            {item.status === 'duplicate' && (
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                Already in library – linked to category
                              </Typography>
                            )}
                            {item.status === 'error' && (
                              <Typography variant="caption" sx={{ color: '#ef4444' }} noWrap>
                                {item.error}
                              </Typography>
                            )}
                          </Box>

                          {/* Right-side % or status */}
                          <Typography variant="caption" sx={{ color: '#666', flexShrink: 0, minWidth: 40, textAlign: 'right' }}>
                            {item.status === 'uploading' ? `${item.progress}%`
                              : item.status === 'done'      ? '✓'
                              : item.status === 'duplicate' ? '↩'
                              : item.status === 'error'     ? 'failed'
                              : '—'}
                          </Typography>
                        </Box>
                      ))}
                          {uploadQueue.length > visibleUploadQueue.length && (
                            <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid var(--rs-surface)' }}>
                              <Typography variant="caption" sx={{ color: '#888' }}>
                                Showing first {visibleUploadQueue.length} files. {uploadQueue.length - visibleUploadQueue.length} more in queue.
                              </Typography>
                            </Box>
                          )}
                    </Box>

                    {/* Summary footer */}
                    {!uploadingActive && (
                      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid var(--rs-border)', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {qDone > 0 && (
                          <Typography variant="caption" sx={{ color: '#22c55e' }}>
                            {qDone} uploaded
                          </Typography>
                        )}
                        {qDuplicates > 0 && (
                          <Typography variant="caption" sx={{ color: '#fbbf24' }}>
                            {qDuplicates} already in library
                          </Typography>
                        )}
                        {qFailed > 0 && (
                          <Typography variant="caption" sx={{ color: '#ef4444' }}>
                            {qFailed} failed
                          </Typography>
                        )}
                      </Box>
                    )}
                  </DialogContent>

                  <DialogActions>
                    <Button
                      onClick={handleCloseBulkUploadDialog}
                      disabled={uploadingActive}
                      variant={uploadingActive ? 'text' : 'contained'}
                      sx={uploadingActive ? {} : { bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
                    >
                      {uploadingActive ? 'Uploading…' : 'Close'}
                    </Button>
                  </DialogActions>
                </>
              )}
            </Dialog>
          );
        })()}

        {/* ── Upload Progress Panel (floating bottom-right) ── */}
        {uploadPanelOpen && (() => {
          const total = queueStats.total;
          const done = queueStats.done;
          const duplicates = queueStats.duplicates;
          const failed = queueStats.failed;
          const pending = queueStats.pending;
          const overallPct = queueStats.overallPct;
          return (
            <Box sx={{
              position: 'fixed', bottom: 24, right: 24,
              width: 380, zIndex: 1400,
              bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)',
              borderRadius: 2, boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {/* Header */}
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid var(--rs-border)' }}>
                {uploadingActive
                  ? <CircularProgress size={16} sx={{ color: '#22d3ee' }} />
                  : failed > 0
                    ? <ErrorOutline sx={{ fontSize: 18, color: '#ef4444' }} />
                    : <CheckCircle sx={{ fontSize: 18, color: '#22c55e' }} />
                }
                <Typography variant="subtitle2" sx={{ flex: 1, color: 'var(--rs-text)' }}>
                  {uploadingActive
                    ? `Uploading… ${done + duplicates} / ${total}`
                    : failed > 0
                      ? `Done — ${done} uploaded, ${duplicates > 0 ? `${duplicates} existing, ` : ''}${failed} failed`
                      : duplicates > 0
                        ? `Done — ${done} uploaded, ${duplicates} already in library`
                        : `All ${total} file${total === 1 ? '' : 's'} uploaded`
                  }
                </Typography>
                <Tooltip title={uploadPanelExpanded ? 'Collapse' : 'Expand'}>
                  <IconButton size="small" onClick={() => setUploadPanelExpanded(v => !v)} sx={{ color: 'var(--rs-muted)' }}>
                    {uploadPanelExpanded ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={uploadingActive ? 'Close when finished' : 'Close'}>
                  <span>
                    <IconButton size="small" onClick={() => setUploadPanelOpen(false)} disabled={uploadingActive} sx={{ color: 'var(--rs-muted)' }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Overall progress bar */}
              <LinearProgress
                variant="determinate"
                value={overallPct}
                sx={{
                  height: 3,
                  bgcolor: 'var(--rs-border)',
                  '& .MuiLinearProgress-bar': { bgcolor: failed > 0 ? '#ef4444' : '#22d3ee' },
                }}
              />

              {/* Per-file list */}
              <Collapse in={uploadPanelExpanded}>
                <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                  {visibleUploadQueue.map(item => (
                    <Box
                      key={item.id}
                      sx={{
                        px: 2, py: 1,
                        borderBottom: '1px solid var(--rs-surface)',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        bgcolor: item.status === 'error'     ? 'rgba(244,67,54,0.06)'
                               : item.status === 'duplicate' ? 'rgba(255,193,7,0.05)'
                               : 'transparent',
                      }}
                    >
                      {/* Status icon */}
                      {item.status === 'done'      && <CheckCircle sx={{ fontSize: 16, color: '#22c55e', flexShrink: 0 }} />}
                      {item.status === 'duplicate' && (
                        <Tooltip title="Already in library – added to category">
                          <LibraryAdd sx={{ fontSize: 16, color: '#fbbf24', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      {item.status === 'error'     && (
                        <Tooltip title={item.error}>
                          <ErrorOutline sx={{ fontSize: 16, color: '#ef4444', flexShrink: 0 }} />
                        </Tooltip>
                      )}
                      {item.status === 'uploading' && <CircularProgress size={14} sx={{ color: '#22d3ee', flexShrink: 0 }} />}
                      {item.status === 'queued'    && <HourglassEmpty  sx={{ fontSize: 16, color: '#5b6675', flexShrink: 0 }} />}

                      {/* Filename + per-file progress bar */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          noWrap
                          title={item.name}
                          sx={{
                            color: item.status === 'error'     ? '#ef4444'
                                 : item.status === 'duplicate' ? '#fbbf24'
                                 : item.status === 'done'      ? 'var(--rs-muted)'
                                 : 'var(--rs-text)',
                            display: 'block',
                          }}
                        >
                          {item.name}
                        </Typography>
                        {item.status === 'uploading' && (
                          <LinearProgress
                            variant="determinate"
                            value={item.progress}
                            sx={{ mt: 0.5, height: 2, bgcolor: 'var(--rs-border)', '& .MuiLinearProgress-bar': { bgcolor: '#22d3ee' } }}
                          />
                        )}
                        {item.status === 'duplicate' && (
                          <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }}>
                            Already in library – linked to category
                          </Typography>
                        )}
                      </Box>

                      {/* Right-side label */}
                      <Typography variant="caption" sx={{ color: '#666', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
                        {item.status === 'uploading' ? `${item.progress}%`
                          : item.status === 'done'      ? '✓'
                          : item.status === 'duplicate' ? '↩'
                          : item.status === 'error'     ? 'fail'
                          : '—'}
                      </Typography>
                    </Box>
                  ))}
                  {uploadQueue.length > visibleUploadQueue.length && (
                    <Box sx={{ px: 2, py: 1 }}>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        Showing first {visibleUploadQueue.length} files. {uploadQueue.length - visibleUploadQueue.length} more in queue.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Footer summary */}
                <Box sx={{ px: 2, py: 1, borderTop: '1px solid var(--rs-border)', display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: '#22c55e' }}>{done} uploaded</Typography>
                  {duplicates > 0 && <Typography variant="caption" sx={{ color: '#fbbf24' }}>{duplicates} existing</Typography>}
                  {failed > 0 && <Typography variant="caption" sx={{ color: '#ef4444' }}>{failed} failed</Typography>}
                  {pending > 0 && <Typography variant="caption" sx={{ color: '#888' }}>{pending} remaining</Typography>}
                </Box>
              </Collapse>
            </Box>
          );
        })()}

        {/* Bulk Category Assignment Dialog */}
        <Dialog 
          open={bulkCategoryDialogOpen} 
          onClose={() => {
            if (bulkUpdating) return;
            setBulkCategoryDialogOpen(false);
            setBulkCategoryTypeFilter('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Assign Categories to {selectedAssets.length} Asset{selectedAssets.length === 1 ? '' : 's'}</DialogTitle>
          <DialogContent>
            {categories.length > CATEGORY_TYPE_FILTER_THRESHOLD && (
              <CategoryTypeSelect
                value={bulkCategoryTypeFilter}
                onChange={setBulkCategoryTypeFilter}
                fullWidth
                sx={{ mt: 2, mb: 2 }}
              />
            )}
            <FormControl fullWidth sx={{ mt: categories.length > CATEGORY_TYPE_FILTER_THRESHOLD ? 0 : 2 }}>
              <InputLabel>Categories</InputLabel>
              <Select
                multiple
                value={bulkCategoryIds}
                onChange={(e) => setBulkCategoryIds(e.target.value)}
                label="Categories"
                MenuProps={categoryMenuProps}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const category = categoriesById.get(id);
                      return (
                        <Chip
                          key={id}
                          label={category?.name || id}
                          size="small"
                          sx={{
                            bgcolor: category?.color || '#1976D2',
                            color: 'var(--rs-text)',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {renderGroupedCategoryMenuItems(
                  filterCategoriesByType(categories, bulkCategoryTypeFilter),
                  (category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={bulkCategoryIds.includes(category.id)} />
                      <Chip
                        label={category.name}
                        size="small"
                        sx={{
                          bgcolor: category.color || '#1976D2',
                          color: 'var(--rs-text)',
                          ml: 1,
                        }}
                      />
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Selected categories will be assigned to all {selectedAssets.length} selected asset{selectedAssets.length === 1 ? '' : 's'}.
              This will replace any existing category assignments.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setBulkCategoryDialogOpen(false);
                setBulkCategoryIds([]);
                setBulkCategoryTypeFilter('');
              }}
              disabled={bulkUpdating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAssignCategory}
              variant="contained"
              disabled={bulkCategoryIds.length === 0 || bulkUpdating}
            >
              {bulkUpdating ? 'Assigning...' : 'Assign Categories'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload complete toast — fires when bulk upload finishes inside the dialog */}
        <Snackbar
          open={uploadDoneToast}
          autoHideDuration={4000}
          onClose={() => setUploadDoneToast(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          message={(() => {
            const done = queueStats.done;
            const dups = queueStats.duplicates;
            const fail = queueStats.failed;
            if (fail > 0) return `Upload finished — ${done} uploaded, ${fail} failed`;
            if (dups > 0) return `Upload finished — ${done} uploaded, ${dups} already in library`;
            return `${done} file${done === 1 ? '' : 's'} uploaded successfully`;
          })()}
        />
      </Box>)}

      {/* ========================== REPORTS TAB ========================== */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#22d3ee', mb: 2 }}>Report Filters</Typography>

              {/* Search first: typing here narrows the report and the asset
                  picker below, so finding one track no longer means scrolling
                  a list of everything that aired. */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search title, artist or asset number…"
                    value={reportSearch}
                    onChange={(e) => setReportSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                      endAdornment: reportSearch ? (
                        <IconButton size="small" onClick={() => setReportSearch('')} aria-label="Clear search">
                          <Close fontSize="small" />
                        </IconButton>
                      ) : null,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--rs-overlay-soft)',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#22d3ee' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Title"
                    placeholder="Title contains…"
                    value={reportTitleFilter}
                    onChange={(e) => setReportTitleFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Artist"
                    placeholder="Artist contains…"
                    value={reportArtistFilter}
                    onChange={(e) => setReportArtistFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From"
                    value={reportFrom}
                    onChange={(e) => setReportFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To"
                    value={reportTo}
                    onChange={(e) => setReportTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={reportGroupBy}
                      label="Group By"
                      onChange={(e) => setReportGroupBy(e.target.value)}
                    >
                      <MenuItem value="asset">Asset</MenuItem>
                      <MenuItem value="category">Category</MenuItem>
                      <MenuItem value="hour">Hour of Day</MenuItem>
                      <MenuItem value="day">Day</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {stations.length > 1 && (
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Station</InputLabel>
                      <Select
                        value={reportStationId}
                        label="Station"
                        onChange={(e) => setReportStationId(e.target.value)}
                      >
                        <MenuItem value="">All Stations</MenuItem>
                        {stations.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={2}>
                  <CategoryTypeSelect
                    value={reportCategoryTypeFilter}
                    onChange={setReportCategoryTypeFilter}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={reportCategoryId}
                      label="Category"
                      onChange={(e) => setReportCategoryId(e.target.value)}
                      MenuProps={categoryMenuProps}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {renderGroupedCategoryMenuItems(
                        filterCategoriesByType(reportCategories, reportCategoryTypeFilter),
                        (c) => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    options={reportAssetOptions}
                    value={selectedReportAsset}
                    onChange={(e, value) => handleReportAssetChange(value)}
                    getOptionLabel={formatReportAssetLabel}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    filterOptions={reportAssetFilterOptions}
                    noOptionsText="No tracks aired for these filters"
                    sx={{ width: '100%' }}
                    renderOption={({ key, ...props }, option) => (
                      <Box component="li" key={option.id} {...props}>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="body2" noWrap>{option.title || 'Untitled'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {option.artist || 'Unknown artist'} · #{option.id}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#22d3ee', ml: 1, flexShrink: 0 }}>
                          {formatReportPlays(option)}
                        </Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Asset"
                        placeholder="Search aired tracks"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      onClick={runReport}
                      disabled={reportLoading || logLoading}
                      sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' }, flex: 1 }}
                    >
                      {(reportLoading || logLoading) ? <CircularProgress size={20} color="inherit" /> : 'Run Report'}
                    </Button>
                    {hasReportFilters && (
                      <Button variant="outlined" onClick={clearReportFilters}>
                        Clear filters
                      </Button>
                    )}
                    {reportData && reportData.rows && reportData.rows.length > 0 && (
                      <>
                        <Tooltip title="Download summary as Excel">
                          <IconButton
                            onClick={downloadReportExcel}
                            sx={{ color: '#22c55e', border: '1px solid #22c55e', borderRadius: 1 }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download summary as PDF">
                          <IconButton
                            onClick={downloadReportPdf}
                            sx={{ color: '#ff5252', border: '1px solid #ff5252', borderRadius: 1 }}
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ minWidth: 240, flex: '1 1 320px' }}>
                <Typography variant="h6" sx={{ color: '#22d3ee' }}>Items in each category</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Download the assets in each category as Excel or PDF. Each category is its own sheet or section, with a combined list as well. Station, category type, category, the search above, and the dates apply. Plays and airtime are for that date range.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={catalogueDownloading === 'excel' ? <CircularProgress size={16} color="inherit" /> : <Download />}
                  disabled={Boolean(catalogueDownloading)}
                  onClick={() => downloadLibraryCatalogue('excel')}
                  sx={{ borderColor: '#22c55e', color: '#22c55e' }}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={catalogueDownloading === 'pdf' ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                  disabled={Boolean(catalogueDownloading)}
                  onClick={() => downloadLibraryCatalogue('pdf')}
                  sx={{ borderColor: '#ff5252', color: '#ff5252' }}
                >
                  PDF
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* View toggle: aggregated summary vs chronological play log */}
          <Tabs
            value={reportView}
            onChange={(e, v) => handleReportViewChange(v)}
            sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            <Tab label="Summary" value="summary" />
            <Tab label="Play Log" value="log" />
          </Tabs>

          {/* What is narrowing the numbers below */}
          {reportFilterSummary && (
            <Chip
              icon={<Search fontSize="small" />}
              label={`Filtered: ${reportFilterSummary}`}
              onDelete={clearReportFilters}
              sx={{ mb: 2, maxWidth: '100%', bgcolor: 'rgba(34,211,238,0.12)', color: '#22d3ee' }}
            />
          )}

          {/* Stats summary */}
          {reportView === 'summary' && reportData && reportData.stats && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Plays', value: reportData.stats.total_plays || 0, color: '#22d3ee' },
                { label: 'Unique Assets', value: reportData.stats.unique_assets || 0, color: '#22c55e' },
                { label: 'Total Airtime', value: formatSeconds(reportData.stats.total_airtime_seconds), color: '#f59e0b' },
              ].map((s) => (
                <Card key={s.label} sx={{ flex: '1 1 160px', minWidth: 160 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>{s.label}</Typography>
                    <Typography variant="h5" className="tabular-nums" sx={{ color: s.color, fontWeight: 700 }}>{s.value}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Report results table */}
          {reportView === 'summary' && reportData && (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                {reportData.rows.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No data found for the selected period. Assets will appear here once they start playing on air.
                    {hasReportFilters
                      ? ' Try clearing the search or filters to see every track that aired.'
                      : ''}
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {{ asset: 'Asset', category: 'Category', hour: 'Hour', day: 'Date' }[reportGroupBy] || 'Item'}
                          </TableCell>
                          {reportGroupBy === 'asset' && <TableCell>Artist</TableCell>}
                          {reportGroupBy === 'asset' && <TableCell>Categories</TableCell>}
                          <TableCell align="right">Plays</TableCell>
                          <TableCell align="right">Total Airtime</TableCell>
                          {reportGroupBy === 'asset' && (
                            <Tooltip title="Average time between plays over the selected period">
                              <TableCell align="right">Avg Rotation</TableCell>
                            </Tooltip>
                          )}
                          <TableCell>First Play</TableCell>
                          <TableCell>Last Play</TableCell>
                          {reportGroupBy === 'asset' && <TableCell>Details</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.rows.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                            <TableCell>
                              {reportGroupBy === 'hour'
                                ? `${String(row.group_label).padStart(2, '0')}:00 – ${String(row.group_label).padStart(2, '0')}:59`
                                : reportGroupBy === 'asset'
                                  ? `Asset ${row.group_id || '—'} — ${row.group_label || 'Untitled'}`
                                  : row.group_label || 'Unknown'}
                            </TableCell>
                            {reportGroupBy === 'asset' && <TableCell>{row.artist || '—'}</TableCell>}
                            {reportGroupBy === 'asset' && (
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {(row.category_names || []).map((name, ci) => (
                                    <Chip key={ci} label={name} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  ))}
                                </Box>
                              </TableCell>
                            )}
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#22d3ee' }}>
                              {row.play_count}
                            </TableCell>
                            <TableCell align="right">{formatSeconds(row.total_duration_seconds)}</TableCell>
                            {reportGroupBy === 'asset' && (
                              <TableCell align="right" sx={{ color: '#ff9800' }}>{formatGap(row.avg_gap_seconds)}</TableCell>
                            )}
                            <TableCell>
                              <Typography variant="caption">{row.first_play ? formatDateTime(row.first_play) : '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{row.last_play ? formatDateTime(row.last_play) : '—'}</Typography>
                            </TableCell>
                            {reportGroupBy === 'asset' && (
                              <TableCell>
                                {row.group_id && (
                                  <Tooltip title="View play history">
                                    <IconButton size="small" onClick={() => fetchAssetDetail(row.group_id)} sx={{ color: '#22d3ee' }}>
                                      <Search fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* Play log (chronological) view */}
          {reportView === 'log' && (
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                {logLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress sx={{ color: '#22d3ee' }} />
                  </Box>
                )}
                {!logLoading && logRows !== null && logRows.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      No plays found for the selected filters and period.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The asset picker lists tracks that actually aired. If Summary is also empty, widen the dates or choose All Stations.
                    </Typography>
                    {hasReportFilters && (
                      <Button variant="outlined" onClick={clearReportFilters}>
                        Clear search and filters
                      </Button>
                    )}
                  </Box>
                )}
                {!logLoading && logRows !== null && logRows.length > 0 && (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Played At</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Artist</TableCell>
                            {stations.length > 1 && <TableCell>Station</TableCell>}
                            <TableCell>Categories</TableCell>
                            <TableCell align="right">Duration</TableCell>
                            <TableCell>Source</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {logRows.map((play) => (
                            <TableRow key={play.id} sx={{ '&:hover': { bgcolor: 'var(--rs-row)' } }}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {new Date(play.played_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' })}
                              </TableCell>
                              <TableCell>{play.title || 'Untitled'}</TableCell>
                              <TableCell>{play.artist || '—'}</TableCell>
                              {stations.length > 1 && (
                                <TableCell>{play.station_name || (play.station_id ? `Station ${play.station_id}` : '—')}</TableCell>
                              )}
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {(play.category_names || []).map((name, ci) => (
                                    <Chip key={ci} label={name} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell align="right">{formatSeconds(play.duration_seconds)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={play.source || 'scheduler'}
                                  size="small"
                                  sx={{
                                    fontSize: '0.65rem',
                                    bgcolor: play.source === 'ad_break' ? '#ff980020' : play.source === 'voicetrack' ? '#9c27b020' : '#22d3ee20',
                                    color: play.source === 'ad_break' ? '#ff9800' : play.source === 'voicetrack' ? '#9c27b0' : '#22d3ee',
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={logTotal}
                      page={logPage}
                      onPageChange={(e, newPage) => fetchPlayLog(newPage)}
                      rowsPerPage={100}
                      rowsPerPageOptions={[100]}
                    />
                  </>
                )}
                {logRows === null && !logLoading && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Click "Run Report" to load the play log.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Asset play detail dialog */}
          <Dialog
            open={!!reportDetailAssetId}
            onClose={() => { setReportDetailAssetId(null); setReportDetailData(null); }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: 'var(--rs-paper)', color: '#22d3ee' }}>
              Asset Play History
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'var(--rs-paper)' }}>
              {reportDetailLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#22d3ee' }} />
                </Box>
              )}
              {reportDetailData && (
                <>
                  <Box sx={{ display: 'flex', gap: 3, mb: 2, mt: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Total plays: <strong style={{ color: '#22d3ee' }}>{reportDetailData.stats?.play_count || 0}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total airtime: <strong style={{ color: '#f59e0b' }}>{formatSeconds(reportDetailData.stats?.total_airtime)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg rotation: <strong style={{ color: '#4caf50' }}>{formatGap(reportDetailData.stats?.avg_gap_seconds)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      First play: <strong>{reportDetailData.stats?.first_play ? formatDateTime(reportDetailData.stats.first_play) : '—'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last play: <strong>{reportDetailData.stats?.last_play ? formatDateTime(reportDetailData.stats.last_play) : '—'}</strong>
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Played At</TableCell>
                          <TableCell>Station</TableCell>
                          <TableCell>Duration</TableCell>
                          <Tooltip title="Time since this asset's previous play">
                            <TableCell>Gap</TableCell>
                          </Tooltip>
                          <TableCell>Source</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(reportDetailData.plays || []).map((play) => (
                          <TableRow key={play.id}>
                            <TableCell>{new Date(play.played_at).toLocaleString()}</TableCell>
                            <TableCell>{play.station_name || `Station ${play.station_id}`}</TableCell>
                            <TableCell>{formatSeconds(play.duration_seconds)}</TableCell>
                            <TableCell sx={{ color: '#4caf50' }}>{formatGap(play.gap_seconds)}</TableCell>
                            <TableCell>
                              <Chip
                                label={play.source || 'scheduler'}
                                size="small"
                                sx={{
                                  fontSize: '0.65rem',
                                  bgcolor: play.source === 'ad_break' ? '#f59e0b20' : play.source === 'voicetrack' ? '#a855f720' : '#22d3ee20',
                                  color: play.source === 'ad_break' ? '#f59e0b' : play.source === 'voicetrack' ? '#a855f7' : '#22d3ee',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'var(--rs-paper)' }}>
              <Button onClick={() => { setReportDetailAssetId(null); setReportDetailData(null); }}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ========================== BEATMIX TAB ========================== */}
      {beatmixEnabled && activeTab === 2 && (
        <Box>
          {stations.length > 1 && (
            <FormControl sx={{ minWidth: 200, mb: 2 }}>
              <InputLabel>Station</InputLabel>
              <Select
                value={selectedStation}
                onChange={(e) => {
                  setSelectedStation(e.target.value);
                  const station = stations.find(s => String(s.id) === String(e.target.value));
                  setBeatmixEnabled(station?.beatmix_enabled || false);
                }}
                label="Station"
              >
                {stations.filter(s => s.beatmix_enabled).map((station) => (
                  <MenuItem key={station.id} value={station.id}>
                    {station.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <BeatmixManager
            stationId={selectedStation || (stations.find(s => s.beatmix_enabled)?.id) || ''}
            categories={categories.filter(c => !selectedStation || !c.station_id || String(c.station_id) === String(selectedStation))}
          />
        </Box>
      )}

      </Box>
    );
  }
